// Mountable, interactive supply-run view: the turn-based infiltration layer
// (buildings-by-scavenge-state, occluded vision cones, drag-to-order,
// multi-turn waypoint previews, enemy ghosts) mounted directly on the app's
// live MapLibre map — so starting a supply run is "zoom in on this same map," not
// "swap to a different renderer." Ported from a hand-rolled canvas 2D engine
// (see git history pre-v4-merge-overworld-tactical) to native MapLibre
// sources/layers/markers; behavior is preserved verbatim except where GL
// gives it to us for free (tweened movement is a CSS transition on marker
// transforms, same technique V3 used for mission-car markers — no more
// hand-rolled requestAnimationFrame easing).
//
// This module owns ONLY the tactical map layer: the live tactical state,
// staged orders, selection, and the map interactions specific to supply runs
// (drag-to-order, the scavenge menu). It also owns disabling/re-enabling the
// map's own navigation handlers for the supply run's duration (dragPan etc. would
// otherwise fight with drag-to-order) — everything else about the supply run
// transition (camera flyTo, hiding overworld chrome) is the host's job, via
// mount()/destroy(). Surrounding chrome (unit list, log, status, summary,
// buttons) stays with the host, which wires its controls to the returned API
// and re-renders its chrome from the `onChange`/`onLog`/`onSessionOver` hooks.

import { resolveTacticalTurn, isTacticalSessionOver } from "./tactical-turn.mjs";
import { ENEMY_STATES, enemyVisionFor, ENEMY_CONTACT_METERS, DEFENSE_CONTACT_METERS } from "./tactical-enemy-ai.mjs";
import { isWithinVisionCone, occludedConeRing } from "./vision-cone.mjs";
import { advanceWithTerrainCost, pathIndexForPosition } from "./tactical-movement.mjs";
import { findTerrainPath, buildingDoorsteps } from "./tactical-pathfinding.mjs";
import { pointInBuildings, ringsOf } from "./line-of-sight.mjs";
import { buildingKey } from "./building-scavenge.mjs";
import { professionName } from "./professions-v4.mjs";
import { getItem, ITEM_CATEGORIES, itemCategoryLabel, itemPreviewReveal } from "./items.mjs";
import { EXTRACTION_BAND_METERS, equipCarriedItem, unequipToBag } from "./tactical-session.mjs";
import { distanceInMeters, findRouteBetweenPoints, geodesicCircle, nearestPointOnPolyline } from "../road-graph.mjs";

const PICKUP_CLICK_RADIUS_METERS = 12; // click/drag tolerance to stage a pickup
const MAX_PREVIEW_TURNS = 12;
// Scout vision: a building's real scavenge/survivor status stays hidden until a
// squad member gets within this radius of its footprint — then it "pops" from
// unknown to revealed, with a celebratory popup naming it and its status. While
// you're moving a unit, this radius is previewed as a ring around the move
// destination so you can see what arriving there will reveal.
// (This is the reveal-gating deferred in building-scavenge.mjs's header note.)
const SCOUT_VISION_RADIUS_METERS = 10;
// A freshly discovered building keeps a bright discovery outline this long...
const DISCOVERY_PULSE_MS = 1500;
// ...and the floating name/status popup over it lives this long before fading.
const DISCOVERY_POPUP_MS = 2200;
// A recruited companion renders exactly this many pixels behind its leader, so
// the spacing reads the same at any zoom (a unit marker is ~16px wide). Screen-
// space, not metres, so "a unit width apart" stays true regardless of the supply run
// camera. Tune this to taste.
const COMPANION_GAP_PX = 14;
// Hover-to-preview movement throttles the per-cursor-move pathfinding + redraw
// to this interval so dragging the trail around stays cheap.
const PREVIEW_RENDER_MS = 70;
// Enemy phase: each enemy pulses, then moves/rotates, then the sequence pauses
// before the next one — so you can read one enemy's turn at a time.
const ENEMY_PULSE_MS = 1000; // flash/pulse before it acts
const ENEMY_MOVE_MS = 420; // the move/rotation tween
const ENEMY_SETTLE_MS = 500; // pause after, before the next enemy
const ENEMY_MOVE_FRAMES = 7; // interpolation steps across ENEMY_MOVE_MS
// Enemies that hold their position and facing this turn (e.g. an idle guard)
// skip the move tween entirely and get a much quicker pulse+settle instead,
// so a screen full of stationary guards doesn't burn several seconds doing
// nothing visible.
const ENEMY_PULSE_STATIONARY_MS = 400;
const ENEMY_SETTLE_STATIONARY_MS = 250;
const STATIONARY_EPS_METERS = 0.5;
// How long a struck marker shakes/flashes when it takes (or lands) a melee hit.
const STRUCK_MS = 320;
// Click radius for targeting an enemy: a click within this of an enemy becomes
// an attack order instead of a move, so you don't need pixel-perfect aim.
const ENEMY_TARGET_CLICK_METERS = 18;
// EXTRACTION_BAND_METERS (from tactical-session) is the ring just outside the supply-run
// circle whose streets are the green "exit lanes" — the nav grid makes them
// walkable, tactical-turn auto-extracts a unit that ends its turn there, and
// we fog everything past the band's outer edge to pull focus onto the circle.
const FOG_COLOR = "#0b0f12";
const FOG_OPACITY = 0.5;
// Extracted unit markers fade immediately once the turn resolves.
const EXTRACT_SLIDE_MS = 0;
const EXTRACT_FADE_MS = 650;

const ITEM_CATEGORY_COLORS = {
  [ITEM_CATEGORIES.valuables]: "#e0c96a",
  [ITEM_CATEGORIES.supplies]: "#6fdc8c",
  [ITEM_CATEGORIES.materials]: "#a8b0b7",
  [ITEM_CATEGORIES.tools]: "#6ab0e0",
};
const DEFAULT_ITEM_COLOR = "#d8e0e6";

const ORDER_PATH_COLORS = { extract: "#6fdc8c", scavenge: "#e0a06a", interact: "#e0c96a", move: "#8ab0e0", attack: "#ff5a5a" };

// Rough footprint span (bbox diagonal, metres) of a building polygon — used to
// scale the ransacked "X" so a big building gets a big X and a small one a small
// X. Falls back to a mid value for degenerate geometry.
function buildingSpanMeters(feature) {
  const ring = feature?.geometry?.coordinates?.[0];
  if (!Array.isArray(ring) || ring.length < 2) return 24;
  let minLon = Infinity, minLat = Infinity, maxLon = -Infinity, maxLat = -Infinity;
  for (const [lon, lat] of ring) {
    if (lon < minLon) minLon = lon;
    if (lon > maxLon) maxLon = lon;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }
  return distanceInMeters([minLon, minLat], [maxLon, maxLat]);
}

// Filled/empty HP pips, matching the survivor-card pips (.hp-pip). Rendered above
// a unit/enemy marker so combat damage reads at a glance.
function healthPipsHtml(health) {
  const max = Math.max(1, health?.max ?? 5);
  const current = Math.max(0, Math.min(max, health?.current ?? max));
  let html = "";
  for (let i = 0; i < max; i += 1) html += `<span class="hp-pip${i < current ? "" : " empty"}"></span>`;
  return html;
}

const EMPTY_FC = { type: "FeatureCollection", features: [] };
const featureCollection = (features) => ({ type: "FeatureCollection", features });

// Source/layer ids, all namespaced so mount()/destroy() can add/remove
// exactly what this module owns without touching the host's own layers.
const SRC = {
  fog: "tactical-fog",
  bandFog: "tactical-band-fog",
  corridors: "tactical-corridors",
  zoneBoundary: "tactical-zone-boundary",
  outerRing: "tactical-outer-ring",
  extractionStreets: "tactical-extraction-streets",
  visionRanges: "tactical-vision-ranges",
  moveRange: "tactical-move-range",
  buildings: "tactical-buildings",
  items: "supply-run-items",
  cones: "tactical-cones",
  conesGhost: "tactical-cones-ghost",
  visionLines: "tactical-vision-lines",
  ghostLines: "tactical-ghost-lines",
  ghostPoints: "tactical-ghost-points",
  orderPaths: "tactical-order-paths",
  waypoints: "tactical-waypoints",
  // Base-defense overlay (only mounted when config.defense is passed): the base's
  // rooms, walls, doorway swings, and room labels, drawn from host-built GeoJSON.
  defRooms: "tactical-def-rooms",
  defWalls: "tactical-def-walls",
  defDoors: "tactical-def-doors",
  defLabels: "tactical-def-labels",
};

function stateColorFor(behavior) {
  if (behavior === ENEMY_STATES.pursue) return "#ff5a5a";
  if (behavior === ENEMY_STATES.alerted) return "#ff9d3c";
  if (behavior === ENEMY_STATES.search) return "#d8b48a";
  return "#f2d43a";
}

function featureCentroid(feature) {
  const ring = feature.geometry.type === "Polygon" ? feature.geometry.coordinates[0] : feature.geometry.coordinates[0][0];
  return ring.reduce((acc, [lon, lat]) => [acc[0] + lon / ring.length, acc[1] + lat / ring.length], [0, 0]);
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const lerp = (a, b, t) => a + (b - a) * t;
// Interpolate a bearing along its shortest arc (so 350°→10° rotates +20°, not -340°).
function lerpAngle(a, b, t) {
  const delta = ((b - a + 540) % 360) - 180;
  return a + delta * t;
}

// Does a circle of `radiusMeters` centred at `point` touch a building? True if
// the point is inside the footprint, or within the radius of any of its edges —
// i.e. a scout's vision circle overlaps the building enough to reveal it.
function buildingWithinRadius(point, feature, radiusMeters) {
  if (pointInBuildings(point, [feature])) return true;
  for (const ring of ringsOf(feature)) {
    const near = ring.length >= 2 ? nearestPointOnPolyline(point, ring) : null;
    if (near && near.distanceMeters <= radiusMeters) return true;
  }
  return false;
}

function closedRing(ring) {
  return ring.length && ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1]
    ? ring
    : [...ring, ring[0]];
}

// Smallest absolute difference between two bearings in degrees (0..180).
function facingDeltaDegrees(a, b) {
  const delta = Math.abs(((a - b) % 360 + 360) % 360);
  return delta > 180 ? 360 - delta : delta;
}

// Add points along a polyline so it can be filtered by per-point distance
// without missing where it enters/leaves a band between vertices.
function densifyPolyline(coordinates, stepMeters) {
  const out = [];
  for (let i = 0; i < coordinates.length - 1; i += 1) {
    const a = coordinates[i];
    const b = coordinates[i + 1];
    const steps = Math.max(1, Math.ceil(distanceInMeters(a, b) / stepMeters));
    for (let k = 0; k < steps; k += 1) out.push([a[0] + (b[0] - a[0]) * (k / steps), a[1] + (b[1] - a[1]) * (k / steps)]);
  }
  out.push(coordinates[coordinates.length - 1]);
  return out;
}

// The street segments that fall inside the extraction band (the annulus
// [innerR, outerR] around `center`) — the green "exit lanes." Each corridor is
// densified then split into runs of in-band points.
function extractionStreetSegments(corridors, center, innerR, outerR) {
  const segments = [];
  for (const corridor of corridors) {
    let run = [];
    for (const point of densifyPolyline(corridor.coordinates, 3)) {
      const distance = distanceInMeters(point, center);
      if (distance >= innerR && distance <= outerR) {
        run.push(point);
      } else {
        if (run.length >= 2) segments.push(run);
        run = [];
      }
    }
    if (run.length >= 2) segments.push(run);
  }
  return segments;
}

// A fog polygon: a big box around the zone with a circular hole punched at
// `holeRadius`, so everything outside the supply-run circle + extraction band reads
// as dimmed. The box only needs to cover the (locked) supply-run viewport.
function fogPolygon(center, holeRadius) {
  const outer = [
    [center[0] - 0.08, center[1] - 0.06], [center[0] + 0.08, center[1] - 0.06],
    [center[0] + 0.08, center[1] + 0.06], [center[0] - 0.08, center[1] + 0.06],
    [center[0] - 0.08, center[1] - 0.06],
  ];
  return { type: "Feature", properties: {}, geometry: { type: "Polygon", coordinates: [outer, closedRing(geodesicCircle(center, holeRadius, 96))] } };
}

// A ring (annulus) polygon between innerR and outerR — the extraction band,
// given a light-fog tint so it reads as a step between the clear supply-run circle
// and the full fog outside.
function annulusPolygon(center, innerR, outerR) {
  return { type: "Feature", properties: {}, geometry: { type: "Polygon", coordinates: [
    closedRing(geodesicCircle(center, outerR, 96)), closedRing(geodesicCircle(center, innerR, 96)),
  ] } };
}

// config: {
//   map, zone, roadGraph, navGrid, poiCoordinate, poiBuilding, poiEntrances,
//   tunables, createSession, hooks: { onChange?, onLog?(entries), onSessionOver?() },
// }
// `tunables` (coneDegrees, sightMeters, moveMetersPerTurn, offStreetMultiplier)
// is owned by the view after construction; the host reads/writes it via
// getTunables()/setTunable(). `createSession()` returns a fresh tacticalState.
export function createTacticalView(config) {
  const { map, zone, roadGraph, navGrid, poiCoordinate, poiBuilding, poiEntrances, createSession, hooks = {} } = config;
  // Base-defense mode: when present, the mission is a base defense, not a raid.
  // { rooms, walls, doors, labels } are host-built GeoJSON FeatureCollections
  // for the base overlay. Turns off the raid-only extraction band + scout fog;
  // the movement/order/turn machinery is unchanged. See the rebuild plan.
  const defense = config.defense ?? null;
  // Knife-range engagement in a defense so survivors hold their opening instead of
  // being pulled into a wall-side brawl; the raid keeps its looser 8 m melee.
  const meleeRangeMeters = defense ? DEFENSE_CONTACT_METERS : ENEMY_CONTACT_METERS;
  // A supply run's "off-street" penalty models crossing rubble/backyards off the
  // road grid. Inside your own base there are no streets, so that penalty just
  // makes defenders sluggish (a ~15 m budget crawling ~8 m) and reads as "they
  // barely move." A defense pays no penalty — full move budget across the floor.
  const defenseAwareOffStreetMultiplier = () => (defense ? 1 : tunables.offStreetMultiplier);
  // Recruitment gate. Reach-goal/fast-follow (housing): the host may pass a
  // recruitPolicy whose hasHousing() blocks recruiting when the base is full. By
  // default recruiting is always allowed (the core slice ships without housing).
  const recruitPolicy = config.recruitPolicy ?? { hasHousing: () => true };

  const tunables = { ...config.tunables };

  let tacticalState = null;
  // Turn model: orders are SCHEDULED, not executed on issue. Each unit holds a
  // queue of chained orders (shift-click appends); End Turn resolves the head of
  // every queue for one turn (units first, then the animated enemy phase),
  // popping finished orders so a chain advances turn by turn. This is what makes
  // orders cancelable — nothing happens until you End Turn.
  let orderQueues = {}; // unitId -> [order, ...]
  // The cursor's provisional order for the selected unit (hover preview only; not
  // committed to a queue until you click).
  let previewOrder = null; // { unitId, order, chain }
  const skippedUnitIds = new Set(); // units explicitly told to hold this turn (no idle warning)
  // A unit acts (moves one step) the instant it's ordered, then dims — it's spent
  // for the turn. Chained/future steps stay queued and run on End Turn.
  const actedUnitIds = new Set();
  let selectedUnitId = null;
  let spawnedThisTurnIds = new Set(); // threat reinforcements that arrived this turn (pulsed until next turn)
  let destroyed = false; // guards deferred discovery-pulse/popup timers after teardown

  let enemyPhaseRunning = false;
  let enemyPhaseSkip = false; // set by skipEnemyAnimation() to fast-forward the enemy phase
  let phaseSleepCancels = []; // pending enemy-phase sleeps, resolved early on skip
  let actingEnemyId = null; // the enemy currently pulsing before its turn — its vision cone throbs too
  let endangeredUnitIds = new Set(); // units in an enemy cone now, or predicted to enter one next turn — they pulse red

  // Item transfer: a picked-up item awaiting a nearby target unit to hand it to.
  let transferSelection = null; // { fromUnitId, itemId }

  const queueFor = (unitId) => orderQueues[unitId] ?? [];
  const currentOrderFor = (unitId) => orderQueues[unitId]?.[0] ?? null;
  const hasOrders = (unitId) => queueFor(unitId).length > 0;
  function clearUnitEngagement(unitId, { notify = false } = {}) {
    if (!tacticalState?.engagements?.[unitId]) return;
    const unit = tacticalState.units?.[unitId];
    const engagements = { ...tacticalState.engagements };
    delete engagements[unitId];
    tacticalState = { ...tacticalState, engagements };
    if (notify) hooks.onLog?.([{ type: "unit-disengaged", unitId, coordinate: unit?.position ? [...unit.position] : null }], tacticalState.turnNumber);
  }
  function currentOrdersMap() {
    const map = {};
    for (const [unitId, queue] of Object.entries(orderQueues)) if (queue.length) map[unitId] = queue[0];
    return map;
  }
  function commitOrder(unitId, order, chain) {
    skippedUnitIds.delete(unitId);
    clearUnitEngagement(unitId, { notify: true });
    if (chain && orderQueues[unitId]?.length) {
      // Shift-click: append a future step; it runs on a later turn, not now.
      orderQueues[unitId] = [...orderQueues[unitId], order];
      render();
      return;
    }
    // A plain order replaces the queue and, if the unit can still act this turn,
    // executes its first step immediately (moving, uncovering, picking up) before
    // the unit dims. An already-spent unit just re-plans for its next turn.
    orderQueues[unitId] = [order];
    if (!actedUnitIds.has(unitId) && tacticalState.units[unitId]?.status !== "scavenging") executeHeadStep(unitId);
    else render();
  }
  // Where the next chained order should start: the tail of the queue's path when
  // chaining, otherwise the unit's current position.
  function chainOriginFor(unitId, chain) {
    const unit = tacticalState.units[unitId];
    if (!chain || !orderQueues[unitId]?.length) return unit?.position;
    return orderDestination(orderQueues[unitId].at(-1)) ?? unit?.position;
  }

  // Scout-vision fog of building status: a building's real state is hidden until
  // a squad member's vision circle touches it (see SCOUT_VISION_RADIUS_METERS).
  const discoveredBuildingKeys = new Set(); // revealed buildings (stable keys)
  const recentlyDiscoveredKeys = new Set(); // revealed this beat — bright outline
  const discoveryPopupMarkers = new Set(); // live "name · status" popups over reveals
  // The squad spawns already next to a few buildings; those reveal silently on
  // the first pass (they still pop from unknown to their real colour) so the
  // supply run doesn't open with a burst of overlapping popups. Only buildings the
  // player *moves into* view get the celebratory popup + outline pulse.
  let silentReveal = true;

  // Hover-to-preview movement state. Selecting a unit (its marker, or the host's
  // unit list) arms "preview mode": the cursor's live position stages a
  // provisional order so its trail draws before you commit, and a map click
  // locks it in. Two throttles keep the per-move pathfinding + redraw cheap.
  let previewMode = false;
  let previewRenderTimer = null;
  let lastPreviewAt = 0;
  let lastHoverPathAt = 0;
  let suppressMapClickUntil = 0; // ignore the map click that a marker-select bubbles up
  let hoveredBuildingKey = null; // scavengeable/survivor building under the cursor while previewing
  // The "Potential Supplies" tooltip: follows the hovered building while planning a
  // move, or sticks to a building clicked with no unit selected (pinned).
  let pinnedTooltipKey = null;
  let itemTooltipMarker = null;
  let itemTooltipKey = null; // the building key the live tooltip currently describes
  // Enemy next-move is shown on demand only: hover an enemy (or click to pin it)
  // to reveal its predicted move; otherwise a will-move enemy just slow-pulses.
  let hoveredEnemyId = null;
  let pinnedEnemyId = null;
  // The unit under the cursor, for the "in danger" hover tooltip.
  let hoveredUnitId = null;
  let dangerTooltipMarker = null;

  // The small cursor notice/menu shown when scheduling an arrival action (the
  // "will scavenge/extract upon arrival" notices and the recruit confirm). All
  // reuse the same DOM slot + close listeners.
  let scavengeMenuEl = null;

  const unitMarkers = new Map(); // unitId -> { marker, el }
  const extractingMarkers = new Set(); // markers playing the extract-out animation before removal
  const pendingExtractAnimations = new Map(); // unitId -> extract coordinate, captured on the turn a unit leaves
  const enemyMarkers = new Map(); // enemyId -> { marker, el }
  const dyingEnemyIds = new Set(); // enemies the player killed this step — fade their marker out instead of popping
  const companionMarkers = new Map(); // companionId -> { marker, el } — recruited survivors following the squad (not selectable)

  // --- Mount: sources/layers, added once for this supply run's lifetime ---------

  function addEmptySource(id) { map.addSource(id, { type: "geojson", data: EMPTY_FC }); }

  function mountLayers() {
    map.addSource(SRC.corridors, { type: "geojson", data: featureCollection([...zone.corridors.values()].map((corridor) => ({
      type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: corridor.coordinates },
    }))) });
    map.addLayer({ id: `${SRC.corridors}-line`, type: "line", source: SRC.corridors, paint: { "line-color": "#5f7788", "line-width": 2.5, "line-opacity": 0.7 } });

    // Buildings render before the zone/interact circles and crossings so
    // those thin outlines stay visible on top of the opaque building fill
    // (matching the old canvas draw order — circles were drawn after
    // buildings there too).
    map.addSource(SRC.buildings, { type: "geojson", data: featureCollection(zone.buildings) });
    map.addLayer({
      id: `${SRC.buildings}-fill`, type: "fill", source: SRC.buildings,
      paint: {
        "fill-color": [
          "case",
          // Not yet scouted: a muted, uniform "unknown" slate — no status leaked.
          ["==", ["get", "discovered"], false], "#2f3841",
          ["==", ["get", "survivorBuilding"], true], "#8b5cf6",
          [">=", ["get", "itemCount"], 2], "#1f8f9b",
          [">", ["get", "itemCount"], 0], "#7ec8cf",
          // Ransacked/empty: a light neutral gray (was a dark slate) so the
          // black "X" reads on top of it and it clearly says "nothing here."
          ["==", ["get", "scavengeState"], "emptied"], "#c2c8cc",
          "#9aa3a9",
        ],
        // Unknown buildings recede (lower opacity); a previewed-reveal one lifts
        // toward solid to signal "this will flip"; revealed ones read solid.
        "fill-opacity": [
          "case",
          ["==", ["get", "previewReveal"], true], 0.78,
          ["==", ["get", "discovered"], false], 0.5,
          0.9,
        ],
      },
    });
    map.addLayer({
      id: `${SRC.buildings}-outline`, type: "line", source: SRC.buildings,
      paint: {
        "line-color": [
          "case",
          // A just-revealed building flashes a bright gold outline (see justDiscovered).
          ["==", ["get", "justDiscovered"], true], "#ffe08a",
          // A previewed-reveal building gets a green "will flip" outline.
          ["==", ["get", "previewReveal"], true], "#5fe08a",
          ["==", ["get", "discovered"], false], "#586470",
          ["==", ["get", "survivorBuilding"], true], "#b794ff",
          ["==", ["get", "targeted"], true], "#c9a4ff",
          [">=", ["get", "itemCount"], 2], "#8ee9f0",
          [">", ["get", "itemCount"], 0], "#d0f5f7",
          ["==", ["get", "scavengeState"], "emptied"], "#404950",
          "#c7ced3",
        ],
        "line-width": [
          "case",
          ["==", ["get", "justDiscovered"], true], 3.5,
          ["==", ["get", "previewReveal"], true], 2.5,
          ["any", ["==", ["get", "survivorBuilding"], true], ["==", ["get", "targeted"], true]], 2.5,
          1,
        ],
      },
    });
    // Plain GeoJSON properties (updated via setData, not feature-state) for
    // all of the above — MapLibre doesn't allow feature-state data
    // expressions in layout properties, and this text-field needs one.
    map.addLayer({
      id: `${SRC.buildings}-count`, type: "symbol", source: SRC.buildings,
      layout: {
        // Stocked buildings show their item count (ransacked ones get an "X" in
        // its own layer below; unknown ones show nothing).
        "text-field": ["case", ["==", ["get", "stocked"], true], ["to-string", ["get", "itemCount"]], ""],
        "text-size": 12,
        "text-allow-overlap": true,
      },
      paint: { "text-color": ["case", [">=", ["get", "itemCount"], 2], "#eaffff", "#0b2428"] },
    });
    // Ransacked/empty buildings get a scrawled grey "X" — its own layer so it can
    // slant (italic) and tilt without leaning the upright count numbers. Italic
    // falls back to the regular face if the basemap glyphs lack it.
    map.addLayer({
      id: `${SRC.buildings}-ransacked`, type: "symbol", source: SRC.buildings,
      layout: {
        "text-field": ["case", ["==", ["get", "scavengeState"], "emptied"], "X", ""],
        "text-font": ["Noto Sans Italic", "Noto Sans Regular"],
        // Scale the X to the building: a ~12m shack gets a small X, a ~60m+ box a
        // big one (clamped by interpolate's end stops).
        "text-size": ["interpolate", ["linear"], ["get", "sizeMeters"], 12, 12, 60, 40],
        "text-rotate": -13,
        "text-allow-overlap": true,
      },
      paint: { "text-color": "#616871", "text-opacity": 0.9 },
    });

    // Inner supply-run circle (dashed). Drawn before the fog so the fog's hole edge
    // reads as the *outer* boundary and this dashed line as the inner one.
    map.addSource(SRC.zoneBoundary, { type: "geojson", data: featureCollection([{
      type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: closedRing(geodesicCircle(zone.centerCoordinate, zone.radiusMeters)) },
    }]) });
    map.addLayer({ id: `${SRC.zoneBoundary}-line`, type: "line", source: SRC.zoneBoundary, layout: { "line-cap": "round" }, paint: { "line-color": "#8a9aa6", "line-width": 1.5, "line-dasharray": [6, 4] } });

    // Fog: everything past the extraction band's outer edge is dimmed, pulling
    // focus onto the supply-run circle. Added above the base streets/buildings (so
    // their out-of-band tails get dimmed too) but below the extraction band and
    // all tactical detail, which sit inside the fog's hole.
    const outerRadius = zone.radiusMeters + EXTRACTION_BAND_METERS;
    map.addSource(SRC.fog, { type: "geojson", data: featureCollection([fogPolygon(zone.centerCoordinate, outerRadius)]) });
    map.addLayer({ id: `${SRC.fog}-fill`, type: "fill", source: SRC.fog, paint: { "fill-color": FOG_COLOR, "fill-opacity": FOG_OPACITY } });

    // Extraction band (supply-run only — a defense has no "leave the circle"
    // exit): the lighter fog tint over the annulus, the green exit-lane streets,
    // and the band's outer ring.
    if (!defense) {
      map.addSource(SRC.bandFog, { type: "geojson", data: featureCollection([annulusPolygon(zone.centerCoordinate, zone.radiusMeters, outerRadius)]) });
      map.addLayer({ id: `${SRC.bandFog}-fill`, type: "fill", source: SRC.bandFog, paint: { "fill-color": FOG_COLOR, "fill-opacity": FOG_OPACITY * 0.45 } });

      map.addSource(SRC.outerRing, { type: "geojson", data: featureCollection([{
        type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: closedRing(geodesicCircle(zone.centerCoordinate, outerRadius)) },
      }]) });
      map.addLayer({ id: `${SRC.outerRing}-line`, type: "line", source: SRC.outerRing, paint: { "line-color": "#4c8a62", "line-width": 1, "line-opacity": 0.7 } });

      const extractionSegments = extractionStreetSegments(zone.corridors.values(), zone.centerCoordinate, zone.radiusMeters, outerRadius);
      map.addSource(SRC.extractionStreets, { type: "geojson", data: featureCollection(extractionSegments.map((coordinates) => ({
        type: "Feature", properties: {}, geometry: { type: "LineString", coordinates },
      }))) });
      map.addLayer({ id: `${SRC.extractionStreets}-line`, type: "line", source: SRC.extractionStreets, layout: { "line-cap": "round" }, paint: { "line-color": "#5fe08a", "line-width": 10, "line-opacity": 0.55 } });
    }

    if (defense) mountDefenseLayers();

    // Each squad member's scout vision radius: a faint fill + dashed ring so the
    // player can see how close they need to get to reveal a building's status.
    addEmptySource(SRC.visionRanges);
    map.addLayer({ id: `${SRC.visionRanges}-fill`, type: "fill", source: SRC.visionRanges, paint: { "fill-color": "#5fe08a", "fill-opacity": 0.2 } });
    map.addLayer({ id: `${SRC.visionRanges}-line`, type: "line", source: SRC.visionRanges, paint: { "line-color": "#4fd07a", "line-width": 1.5, "line-opacity": 0.9, "line-dasharray": [3, 3] } });

    // The selected unit's move range this turn — a soft blue dashed circle so
    // "you can move this far, and that's it" reads at a glance (plan-ahead off).
    addEmptySource(SRC.moveRange);
    map.addLayer({ id: `${SRC.moveRange}-fill`, type: "fill", source: SRC.moveRange, paint: { "fill-color": "#8ab0e0", "fill-opacity": 0.08 } });
    map.addLayer({ id: `${SRC.moveRange}-line`, type: "line", source: SRC.moveRange, paint: { "line-color": "#8ab0e0", "line-width": 1.5, "line-opacity": 0.85, "line-dasharray": [2, 2] } });

    addEmptySource(SRC.items);
    map.addLayer({ id: `${SRC.items}-circle`, type: "circle", source: SRC.items, paint: { "circle-color": ["get", "color"], "circle-radius": 6, "circle-stroke-color": "#10151a", "circle-stroke-width": 1.5 } });
    // Dark halo so the light label reads on the light basemap (it used to sit
    // on the old canvas's near-black background); the halo also keeps it
    // legible if the basemap ever goes dark.
    map.addLayer({ id: `${SRC.items}-label`, type: "symbol", source: SRC.items, layout: { "text-field": ["get", "label"], "text-size": 11, "text-offset": [1, 0], "text-anchor": "left", "text-allow-overlap": true }, paint: { "text-color": "#eef3f6", "text-halo-color": "#0d1117", "text-halo-width": 1.4 } });

    addEmptySource(SRC.cones);
    // A soft outline + a stronger fill so the cone reads clearly on the light
    // basemap (0.16 was too faint once the renderer moved off the dark canvas).
    map.addLayer({ id: `${SRC.cones}-fill`, type: "fill", source: SRC.cones, paint: { "fill-color": ["get", "color"], "fill-opacity": ["get", "fillOpacity"] } });
    map.addLayer({ id: `${SRC.cones}-line`, type: "line", source: SRC.cones, paint: { "line-color": ["get", "color"], "line-width": 1, "line-opacity": 0.5 } });

    addEmptySource(SRC.conesGhost);
    // The ghost (predicted next-turn) cone is now shown only for a hovered/pinned
    // enemy, so it can be a bold, easy-to-read artifact: a faint fill under a
    // thick dashed outline.
    map.addLayer({ id: `${SRC.conesGhost}-fill`, type: "fill", source: SRC.conesGhost, paint: { "fill-color": ["get", "color"], "fill-opacity": 0.16 } });
    map.addLayer({ id: `${SRC.conesGhost}-line`, type: "line", source: SRC.conesGhost, paint: { "line-color": ["get", "color"], "line-width": 2.5, "line-opacity": 0.95, "line-dasharray": [3, 2] } });

    addEmptySource(SRC.visionLines);
    map.addLayer({ id: `${SRC.visionLines}-line`, type: "line", source: SRC.visionLines, paint: { "line-color": "#ff5a5a", "line-width": 1, "line-opacity": 0.8 } });

    addEmptySource(SRC.ghostLines);
    map.addLayer({ id: `${SRC.ghostLines}-line`, type: "line", source: SRC.ghostLines, paint: { "line-color": ["get", "color"], "line-width": 2.5, "line-opacity": 0.9 } });
    addEmptySource(SRC.ghostPoints);
    // circle layers have no dasharray paint property (that's line-only), so
    // the ghost ring is a plain solid stroke — a small simplification from
    // the old canvas's dashed ring.
    map.addLayer({ id: `${SRC.ghostPoints}-circle`, type: "circle", source: SRC.ghostPoints, paint: { "circle-radius": 7, "circle-color": "transparent", "circle-stroke-color": ["get", "color"], "circle-stroke-width": 2.5 } });

    addEmptySource(SRC.orderPaths);
    map.addLayer({ id: `${SRC.orderPaths}-line`, type: "line", source: SRC.orderPaths, paint: { "line-color": ["get", "color"], "line-width": 1.5, "line-dasharray": [3, 3] } });

    addEmptySource(SRC.waypoints);
    map.addLayer({ id: `${SRC.waypoints}-circle`, type: "circle", source: SRC.waypoints, paint: { "circle-radius": 8, "circle-color": "#10151a", "circle-stroke-color": ["get", "color"], "circle-stroke-width": 1.5 } });
    map.addLayer({ id: `${SRC.waypoints}-label`, type: "symbol", source: SRC.waypoints, layout: { "text-field": ["to-string", ["get", "turn"]], "text-size": 11, "text-allow-overlap": true }, paint: { "text-color": ["get", "color"] } });
  }

  function unmountLayers() {
    for (const key of Object.keys(SRC)) {
      const sourceId = SRC[key];
      for (const layerId of allLayerIdsFor(sourceId)) if (map.getLayer(layerId)) map.removeLayer(layerId);
      if (map.getSource(sourceId)) map.removeSource(sourceId);
    }
  }

  // Every layer id this module ever adds for a given source id follows
  // `${sourceId}-<suffix>` — listing the suffixes once here keeps mount and
  // unmount in lockstep without a second hand-maintained id list.
  function allLayerIdsFor(sourceId) {
    return ["-line", "-fill", "-outline", "-count", "-ransacked", "-circle", "-label"].map((suffix) => `${sourceId}${suffix}`);
  }

  // The base-defense overlay: rooms (subtle fill), walls (solid), doorway swings
  // (accent), and room labels — all from host-built GeoJSON so this generic view
  // needs no base-geometry imports. Suffixes match allLayerIdsFor so unmount
  // tears them down automatically.
  function mountDefenseLayers() {
    const empty = featureCollection([]);
    map.addSource(SRC.defRooms, { type: "geojson", data: defense.rooms ?? empty });
    map.addLayer({ id: `${SRC.defRooms}-fill`, type: "fill", source: SRC.defRooms, paint: {
      "fill-color": ["case", ["==", ["get", "safe"], true], "#2f4a3a", ["coalesce", ["get", "color"], "#2b3a46"]],
      "fill-opacity": ["case", ["==", ["get", "breached"], true], 0.15, 0.28],
    } });

    map.addSource(SRC.defWalls, { type: "geojson", data: defense.walls ?? empty });
    map.addLayer({ id: `${SRC.defWalls}-line`, type: "line", source: SRC.defWalls, layout: { "line-cap": "round", "line-join": "round" },
      paint: { "line-color": ["case", ["==", ["get", "kind"], "breached"], "#c96a5a", "#cdd6de"], "line-width": ["case", ["==", ["get", "kind"], "breached"], 1.5, 2.4] } });

    map.addSource(SRC.defDoors, { type: "geojson", data: defense.doors ?? empty });
    map.addLayer({ id: `${SRC.defDoors}-line`, type: "line", source: SRC.defDoors, layout: { "line-cap": "round", "line-join": "round" },
      paint: { "line-color": "#e0c96a", "line-width": 2 } });

    map.addSource(SRC.defLabels, { type: "geojson", data: defense.labels ?? empty });
    map.addLayer({ id: `${SRC.defLabels}-label`, type: "symbol", source: SRC.defLabels,
      layout: { "text-field": ["coalesce", ["get", "label"], ""], "text-size": 11, "text-allow-overlap": true },
      paint: { "text-color": "#aeb8c0", "text-halo-color": "#141b20", "text-halo-width": 1 } });
  }

  // --- Building scavenge helpers ------------------------------------------

  function poolForFeature(feature, index) {
    return tacticalState?.buildingContents?.[buildingKey(feature, index)] ?? null;
  }

  function scavengeableBuildingAt(coordinate) {
    let index = -1;
    for (const feature of zone.buildings) {
      index += 1;
      if (!pointInBuildings(coordinate, [feature])) continue;
      // An unscouted building can't be targeted — it reads as unknown, so
      // offering its scavenge menu would leak that it holds supplies.
      if (!discoveredBuildingKeys.has(buildingKey(feature, index))) continue;
      const pool = poolForFeature(feature, index);
      if (pool && pool.contents.length > 0) return { feature, index, key: buildingKey(feature, index), pool };
    }
    return null;
  }

  // A not-yet-recruited survivor building under the cursor. Checked before
  // scavenge in the drag handlers, so a purple survivor building offers the
  // recruit menu (survivor buildings carry no supply pool, so there's no overlap).
  function survivorBuildingAt(coordinate) {
    const survivors = tacticalState?.survivorBuildings;
    if (!survivors) return null;
    let index = -1;
    for (const feature of zone.buildings) {
      index += 1;
      if (!pointInBuildings(coordinate, [feature])) continue;
      // Same reveal gate as scavenge: don't offer a recruit on an unscouted
      // (still-unknown) building.
      if (!discoveredBuildingKeys.has(buildingKey(feature, index))) continue;
      const key = String(buildingKey(feature, index));
      const descriptor = survivors[key];
      if (descriptor) return { feature, index, key, descriptor };
    }
    return null;
  }

  // --- Routing / order helpers ---------------------------------------------

  // Enemy pathing. A raid's attackers walk the road graph; a defense's raiders
  // path across the base nav grid instead, treating an intact door as a wall —
  // so an attacker must breach its assigned door before it can route through.
  function routeTo(from, to) {
    if (defense) {
      const breached = breachedDoorEdgeSet();
      const result = findTerrainPath(navGrid, from, to, {
        offStreetMultiplier: tunables.offStreetMultiplier,
        doorPassable: (edgeKey) => !edgeKey || breached.has(edgeKey),
      });
      if (Array.isArray(result.coordinates) && result.coordinates.length >= 2) {
        return { reachable: true, coordinates: result.coordinates, distanceMeters: distanceInMeters(from, to), partial: Boolean(result.partial) };
      }
      return { reachable: false, coordinates: [] };
    }
    const route = findRouteBetweenPoints(roadGraph, from, to);
    if (route?.reachable && Array.isArray(route.coordinates) && route.coordinates.length >= 2) return route;
    return { reachable: true, coordinates: [[...from], [...to]], distanceMeters: distanceInMeters(from, to) };
  }

  // Edge keys of doors breached this session (HP gone) — the live gate for
  // raider pathing (breachedDoorEdges reads the same field-object HP the door
  // overlay renders, so movement and the drawn breach never disagree).
  function breachedDoorEdgeSet() {
    const set = new Set();
    for (const object of Object.values(tacticalState?.fieldObjects || {})) {
      if (object.breached && object.edgeKey) set.add(object.edgeKey);
    }
    return set;
  }

  // What blocks sight. A raid occludes on building footprints; a defense occludes
  // on the base's own walls + its INTACT doors (a breached door stops occluding),
  // so an attacker can't see the defenders through a shut door — it assaults the
  // door instead of pursuing through the wall. Rebuilt only when the breach set
  // changes (cheap: a base has a handful of doors), shared by sensing + the cone
  // render so the drawn cone matches what the enemy can actually see.
  let defenseOccluderCache = null; // { signature, features }
  function visionOccluders() {
    if (!defense) return zone.buildings;
    const breached = breachedDoorEdgeSet();
    const signature = [...breached].sort().join("|");
    if (!defenseOccluderCache || defenseOccluderCache.signature !== signature) {
      const features = [];
      for (const segment of defense.wallSegments || []) features.push(segmentOccluder(segment));
      for (const segment of defense.doorSegments || []) {
        if (!breached.has(segment.edgeKey)) features.push(segmentOccluder(segment));
      }
      defenseOccluderCache = { signature, features };
    }
    return defenseOccluderCache.features;
  }

  // What blocks a MELEE STRIKE — only solid walls, never doors. A defender must be
  // able to hit a raider that's chipping their door (you fight the guy breaking in,
  // murder-hole style), so doors — intact or breached — never block a strike; a
  // blank wall does. A raid has no walls (open-ground combat), so it returns null
  // and strikes fall back to pure range. Walls never move, so this is built once.
  let wallStrikeOccluderCache = null;
  function strikeOccluders() {
    if (!defense) return null;
    if (!wallStrikeOccluderCache) wallStrikeOccluderCache = (defense.wallSegments || []).map(segmentOccluder);
    return wallStrikeOccluderCache;
  }

  // A wall/door world segment as a thin quad Polygon feature, so it slots into
  // the same building-footprint occluder pipeline (sightLineBlocked / cone
  // raycast) with no changes to vision-cone. The half-width is a sub-metre
  // sliver in degree space — enough for a sightline crossing the wall line to
  // cross the quad, thin enough not to bleed across an adjacent doorway.
  function segmentOccluder({ a, b }) {
    const dLon = b[0] - a[0];
    const dLat = b[1] - a[1];
    const len = Math.hypot(dLon, dLat) || 1e-9;
    const px = (-dLat / len) * 3e-6;
    const py = (dLon / len) * 3e-6;
    const ring = [
      [a[0] + px, a[1] + py], [b[0] + px, b[1] + py],
      [b[0] - px, b[1] - py], [a[0] - px, a[1] - py], [a[0] + px, a[1] + py],
    ];
    return { type: "Feature", properties: { occluder: "wall" }, geometry: { type: "Polygon", coordinates: [ring] } };
  }

  function buildContext() {
    return {
      zone,
      graph: { corridors: zone.corridors },
      routeTo,
      visionConeDegrees: tunables.coneDegrees,
      sightMeters: tunables.sightMeters,
      occluders: visionOccluders(),
      strikeOccluders: strikeOccluders(),
      defense: Boolean(defense),
      contactRangeMeters: meleeRangeMeters,
      meleeRangeMeters,
      terrain: { corridors: zone.corridors, offStreetMultiplier: defenseAwareOffStreetMultiplier() },
      reinforceOnThreat: !defense, // rising threat pulls new patrols in at the zone edge (raids only; a defense's wave is fixed)
      autoExtractAtBoundary: !defense, // a unit with an extract order that ends its turn on the band leaves (no extraction in a defense)
    };
  }

  function routePath(fromCoord, toCoord) {
    const result = findTerrainPath(navGrid, fromCoord, toCoord, { offStreetMultiplier: tunables.offStreetMultiplier });
    return result.coordinates.length >= 2 ? result.coordinates : [[...fromCoord]];
  }

  // --- Order builders (pure: given a start point, return an order object) -----
  // A chained order starts from the previous order's destination, not the unit,
  // so `from` is passed in (chainOriginFor supplies it).

  function doorstepFrom(from, feature) {
    const doorsteps = buildingDoorsteps(navGrid, feature);
    return doorsteps.length
      ? doorsteps.reduce((best, step) => (distanceInMeters(from, step) < distanceInMeters(from, best) ? step : best))
      : featureCentroid(feature);
  }

  function scavengeOrder(from, feature, key, mode) {
    const path = routePath(from, doorstepFrom(from, feature));
    return { type: "scavenge", buildingId: key, mode, path, target: path[path.length - 1], poiCoordinate: featureCentroid(feature) };
  }

  // Route to a survivor building's doorstep to recruit whoever's inside (resolves
  // on arrival — tactical-turn's "recruit" order → survivor-recruited).
  function recruitOrder(from, feature, key) {
    const path = routePath(from, doorstepFrom(from, feature));
    return { type: "recruit", buildingId: key, path, target: path[path.length - 1], poiCoordinate: featureCentroid(feature) };
  }

  function moveOrder(from, coordinate) {
    const fullPath = routePath(from, coordinate);
    // Plan-ahead is temporarily disabled: cap a move at exactly ONE turn's real
    // reach — the same terrain/off-street-aware step the sim takes — so the drawn
    // path ends right where the unit actually stops this turn. Clamping to a
    // straight-line radius instead left spots inside the move circle that still
    // needed a second turn to reach (roads/off-roading cost more than the crow
    // flies); this makes the path stop at the one-turn point, no leftover leg.
    const unit = tacticalState.units[selectedUnitId];
    const start = from ?? unit?.position ?? fullPath[0];
    const budget = unit?.moveMetersPerTurn ?? tunables.moveMetersPerTurn;
    const terrain = { corridors: zone.corridors, offStreetMultiplier: defenseAwareOffStreetMultiplier() };
    const moved = advanceWithTerrainCost({ position: [...start], pathIndex: pathIndexForPosition(fullPath, start) }, fullPath, budget, terrain);
    const clamped = fullPath.slice(0, moved.pathIndex + 1);
    if (distanceInMeters(clamped[clamped.length - 1], moved.position) > 0.5) clamped.push([...moved.position]);
    return { type: "move", path: clamped, target: [...moved.position] };
  }

  function pickupOrder(from, pickup) {
    const path = routePath(from, pickup.position);
    return { type: "interact", path, target: [...pickup.position], pickupId: pickup.id };
  }

  function attackOrder(from, enemy) {
    // In a defense the survivor holds its ground: a `hold` attack strikes an enemy
    // that comes into reach (with line of sight) but never paths toward it, so it
    // can't be marched out of the base to chase a raider in the open.
    if (defense) return { type: "attack", target: [...enemy.position], enemyId: enemy.id, hold: true };
    const path = routePath(from, enemy.position);
    return { type: "attack", path, target: [...enemy.position], enemyId: enemy.id };
  }

  // The enemy nearest `coordinate` within a comfortable click radius (so you can
  // click at/near an enemy to target it), or null. Used to turn a click into an
  // attack order instead of a move.
  function enemyNearCoordinate(coordinate, maxMeters = ENEMY_TARGET_CLICK_METERS) {
    let best = null;
    for (const enemy of Object.values(tacticalState.enemies)) {
      const d = distanceInMeters(coordinate, enemy.position);
      if (d <= maxMeters && (!best || d < best.d)) best = { enemy, d };
    }
    return best?.enemy ?? null;
  }

  // Order the selected unit to attack a specific enemy (used when an enemy marker
  // is clicked directly). Returns false if no unit is armed, so the caller can
  // fall back to pinning the enemy's predicted-move ghost.
  function attackEnemyWithSelected(enemyId) {
    if (enemyPhaseRunning || !previewMode || !selectedUnitId) return false;
    const unit = tacticalState.units[selectedUnitId];
    const enemy = tacticalState.enemies[enemyId];
    if (!unit || unit.status === "scavenging" || !enemy) return false;
    commitOrder(selectedUnitId, attackOrder(chainOriginFor(selectedUnitId, false), enemy), false);
    previewOrder = null;
    previewMode = false;
    selectedUnitId = null;
    render();
    return true;
  }

  // --- Scavenge / extract / recruit cursor notices -------------------------

  function closeScavengeMenu() {
    if (scavengeMenuEl) { scavengeMenuEl.remove(); scavengeMenuEl = null; }
    document.removeEventListener("pointerdown", onOutsidePointer, true);
    document.removeEventListener("keydown", onMenuKey, true);
  }

  function onOutsidePointer(event) {
    if (scavengeMenuEl && !scavengeMenuEl.contains(event.target)) closeScavengeMenu();
  }
  function onMenuKey(event) {
    if (event.key === "Escape") closeScavengeMenu();
  }

  // A one-line cursor notice (scavenge/extract "…upon arrival"). The order is
  // already scheduled by the click handler; this is just the confirmation.
  function showArrivalNotice(clientX, clientY, text) {
    closeScavengeMenu();
    const menu = document.createElement("div");
    menu.className = "scavenge-menu";
    Object.assign(menu.style, {
      position: "fixed", left: `${clientX}px`, top: `${clientY}px`, zIndex: "1000",
      background: "#1b2127", border: "1px solid #4c8a62", borderRadius: "6px", padding: "6px",
      boxShadow: "0 4px 14px rgba(0,0,0,0.5)", font: "12px system-ui, sans-serif", color: "#d8e0e6", minWidth: "150px",
    });
    const header = document.createElement("div");
    header.textContent = text;
    Object.assign(header.style, { color: "#8fa0ab", padding: "2px 4px" });
    menu.appendChild(header);
    presentMenu(menu);
  }

  // The recruit menu shown on a release over a purple survivor building: a single
  // "Attempt to recruit" action that routes the unit to the building's doorstep.
  // If the base is out of housing (reach-goal gate, off by default) the button is
  // disabled-styled but still pops an alert explaining why.
  function openRecruitMenu(clientX, clientY, unit, target, chain) {
    closeScavengeMenu();
    const menu = document.createElement("div");
    menu.className = "scavenge-menu";
    Object.assign(menu.style, {
      position: "fixed", left: `${clientX}px`, top: `${clientY}px`, zIndex: "1000",
      background: "#1b2127", border: "1px solid #8b5cf6", borderRadius: "6px", padding: "6px",
      boxShadow: "0 4px 14px rgba(0,0,0,0.5)", font: "12px system-ui, sans-serif", color: "#d8e0e6", minWidth: "150px",
    });
    const header = document.createElement("div");
    const profession = professionName(target.descriptor.backgroundId);
    header.textContent = profession ? `${target.descriptor.name} · ${profession}` : `${target.descriptor.name}`;
    Object.assign(header.style, { color: "#b7a6e6", padding: "2px 4px 6px", borderBottom: "1px solid #2a333a", marginBottom: "4px" });
    menu.appendChild(header);

    const hasHousing = recruitPolicy.hasHousing();
    const button = document.createElement("button");
    button.textContent = "Attempt to recruit";
    Object.assign(button.style, {
      display: "block", width: "100%", textAlign: "left", color: "#d8e0e6",
      background: hasHousing ? "#2a333a" : "#242830", border: "1px solid #465059",
      borderRadius: "4px", padding: "6px 8px", margin: "2px 0",
      cursor: "pointer", opacity: hasHousing ? "1" : "0.55",
    });
    button.addEventListener("click", () => {
      if (!recruitPolicy.hasHousing()) {
        // Disabled-but-clickable: explain the block rather than silently no-op.
        alert("Not enough housing at base.");
        return;
      }
      const current = tacticalState.units[unit.id];
      if (current && current.status !== "scavenging") commitOrder(unit.id, recruitOrder(chainOriginFor(unit.id, chain), target.feature, target.key), chain);
      closeScavengeMenu();
      render();
    });
    menu.appendChild(button);

    presentMenu(menu);
  }

  // Whether a coordinate is on/past the extraction band (the release point of a
  // move that should offer to extract).
  function coordinateInExtractionBand(coordinate) {
    if (defense) return false; // a defense has no extraction band — dragging to the edge stays a move
    return distanceInMeters(coordinate, zone.centerCoordinate) >= zone.radiusMeters;
  }

  // Shared: append a cursor menu, keep it on-screen, and wire outside-click /
  // Escape to close (deferred so the release that opened it doesn't close it).
  function presentMenu(menu) {
    document.body.appendChild(menu);
    const rect = menu.getBoundingClientRect();
    if (rect.right > window.innerWidth) menu.style.left = `${window.innerWidth - rect.width - 8}px`;
    if (rect.bottom > window.innerHeight) menu.style.top = `${window.innerHeight - rect.height - 8}px`;
    scavengeMenuEl = menu;
    setTimeout(() => {
      document.addEventListener("pointerdown", onOutsidePointer, true);
      document.addEventListener("keydown", onMenuKey, true);
    }, 0);
  }

  function pickupAtCoordinate(coordinate) {
    let nearest = null;
    let nearestDistance = Infinity;
    for (const pickup of Object.values(tacticalState.scatteredItems)) {
      const distance = distanceInMeters(coordinate, pickup.position);
      if (distance < nearestDistance) { nearest = pickup; nearestDistance = distance; }
    }
    return nearest && nearestDistance <= PICKUP_CLICK_RADIUS_METERS ? nearest : null;
  }

  // --- Preview (pure resolveTacticalTurn, discarded) -----------------------

  function computePreviewState() {
    if (!tacticalState || isTacticalSessionOver(tacticalState)) return null;
    try {
      return resolveTacticalTurn(tacticalState, currentOrdersMap(), buildContext());
    } catch {
      return null;
    }
  }

  // Turn a unit's order chain into drawable segments, each continuous from the
  // previous order's destination (so a shift-chained plan reads as one path).
  // The HEAD order is trimmed to only the part still ahead of the unit, so once
  // it has walked part way the traversed leg disappears and the turn markers
  // renumber from where it actually is.
  function orderChainSegments(unit, orders) {
    let cursor = unit.position;
    const segments = [];
    orders.forEach((order, orderIndex) => {
      if (!order) return;
      const raw = Array.isArray(order.path) && order.path.length >= 2
        ? order.path
        : (Array.isArray(order.target) ? [cursor, order.target] : null);
      if (!raw) return;
      let coords;
      if (orderIndex === 0) {
        // Draw from the unit's current position forward along the remaining path.
        const startIndex = pathIndexForPosition(raw, unit.position);
        const ahead = raw.slice(startIndex + 1).map((point) => [...point]);
        coords = [[...unit.position], ...ahead];
        if (coords.length < 2) { const dest = orderDestination(order); coords = dest ? [[...unit.position], [...dest]] : [[...unit.position]]; }
      } else {
        coords = distanceInMeters(cursor, raw[0]) > 0.5 ? [[...cursor], ...raw.map((point) => [...point])] : raw.map((point) => [...point]);
      }
      segments.push({ order, coords });
      cursor = orderDestination(order) ?? cursor;
    });
    return segments;
  }

  function multiTurnWaypoints(unit, path) {
    const budget = unit.moveMetersPerTurn ?? tunables.moveMetersPerTurn;
    const terrain = { corridors: zone.corridors, offStreetMultiplier: tunables.offStreetMultiplier };
    const waypoints = [];
    let sim = { position: [...unit.position], pathIndex: pathIndexForPosition(path, unit.position) };
    for (let turn = 1; turn <= MAX_PREVIEW_TURNS; turn += 1) {
      const moved = advanceWithTerrainCost(sim, path, budget, terrain);
      if (distanceInMeters(sim.position, moved.position) < 0.5) break;
      waypoints.push({ turn, position: moved.position });
      sim = { position: moved.position, pathIndex: moved.pathIndex };
      if (moved.reachedEnd) break;
    }
    return waypoints;
  }

  // --- Turn control ----------------------------------------------------------

  // After a turn resolves, pop the head order off each queue whose task finished
  // this turn, so a chained order sequence advances one step at a time. A
  // multi-turn task (a scavenge in progress, a move still en route) stays at the
  // head and continues next turn.
  function advanceQueuesAfterTurn(newLog, unitIds = null) {
    const scavengedUnitIds = new Set(
      newLog.filter((effect) => effect.type === "scavenged" || effect.type === "scavenge-finished").map((effect) => effect.unitId),
    );
    const recruitedUnitIds = new Set(
      newLog.filter((effect) => effect.type === "survivor-recruited").map((effect) => effect.unitId),
    );
    for (const unitId of (unitIds ?? Object.keys(orderQueues))) {
      if (!orderQueues[unitId]) continue;
      const unit = tacticalState.units[unitId];
      if (!unit) { delete orderQueues[unitId]; continue; }
      const order = orderQueues[unitId][0];
      if (!order) { delete orderQueues[unitId]; continue; }
      let done = false;
      if (order.type === "interact") done = !tacticalState.scatteredItems[order.pickupId];
      else if (order.type === "scavenge") done = scavengedUnitIds.has(unitId);
      else if (order.type === "recruit") done = recruitedUnitIds.has(unitId) || !tacticalState.survivorBuildings?.[String(order.buildingId)];
      // An attack order retires once its target is dead or gone — otherwise it
      // lingers as a stale "Planned: Attack" pointing at nothing. While the enemy
      // lives the order persists so the unit keeps closing on / holding for it
      // (the engagement a landed strike creates drives the actual round-by-round
      // fighting and releases itself when the enemy dies or breaks off).
      else if (order.type === "attack") done = !tacticalState.enemies[order.enemyId];
      // A plain move consumes one resolved player action. Extract persists until
      // the unit reaches its boundary crossing.
      else if (order.type === "move") done = true;
      else if (order.type === "extract") {
        const destination = order.target ?? order.path?.[order.path.length - 1];
        done = Boolean(destination && distanceInMeters(unit.position, destination) < 1.5);
      } else if (order.type === "drop") done = true;
      if (!done) continue;
      const rest = orderQueues[unitId].slice(1);
      if (rest.length) orderQueues[unitId] = rest;
      else delete orderQueues[unitId];
    }
  }

  function cleanupQueuesForInactiveUnits() {
    for (const unitId of Object.keys(orderQueues)) {
      if (!tacticalState.units[unitId]) delete orderQueues[unitId];
    }
  }

  // Run one unit's head order for a single turn's worth right now (enemies
  // frozen) — the "move immediately, then dim" step. Uncovers buildings / picks
  // up supplies via the resolve + render, pops the order if it finished, and marks
  // the unit spent for this turn.
  function executeHeadStep(unitId) {
    if (enemyPhaseRunning) return;
    const order = orderQueues[unitId]?.[0];
    const unit = tacticalState?.units?.[unitId];
    if (!order || !unit || unit.status === "scavenging") { render(); return; }
    const before = tacticalState;
    tacticalState = resolveTacticalTurn(before, { [unitId]: order }, { ...buildContext(), freezeEnemies: true });
    const newLog = tacticalState.log.slice(before.log.length);
    for (const effect of newLog) {
      if (effect.type === "unit-extracted" && Array.isArray(effect.coordinate)) pendingExtractAnimations.set(effect.unitId, [...effect.coordinate]);
    }
    if (hooks.onLog) hooks.onLog(newLog, tacticalState.turnNumber);
    advanceQueuesAfterTurn(newLog, [unitId]);
    actedUnitIds.add(unitId);
    previewMode = false;
    // Play the melee hit (enemy flash + fade-on-kill) before the render that
    // drops any killed enemy's marker.
    playPlayerAttack(newLog);
    render();
    // Extracting the last survivor ends the supply run mid–player-phase.
    if (isTacticalSessionOver(tacticalState) && hooks.onSessionOver) hooks.onSessionOver();
  }

  // Which unit each enemy caught this turn (contact effects carry the enemyId;
  // a matching unit-lost confirms the kill) — so a death reveals as its killer moves.
  function enemyKillsFromLog(logSlice) {
    const lost = new Set(logSlice.filter((effect) => effect.type === "unit-lost").map((effect) => effect.unitId));
    const kills = {};
    for (const effect of logSlice) {
      if (effect.type === "contact" && effect.targetKind === "survivor" && lost.has(effect.targetId)) {
        (kills[effect.enemyId] ??= []).push(effect.targetId);
      }
    }
    return kills;
  }

  function paintEnemyPhase(displayEnemies) {
    syncEnemiesAndCones(null, displayEnemies);
  }

  function pulseEnemyMarker(enemyId, on) {
    enemyMarkers.get(enemyId)?.el.classList.toggle("acting", on);
  }

  // Shake + red-flash a survivor's marker when it takes a melee hit this phase.
  // Re-adding the class after a forced reflow restarts the keyframe if the same
  // unit is struck on consecutive turns.
  function strikeUnit(unitId) {
    const el = unitMarkers.get(unitId)?.el;
    if (!el) return;
    el.classList.remove("struck");
    void el.offsetWidth; // reflow so the animation replays
    el.classList.add("struck");
    setTimeout(() => { if (!destroyed) el.classList.remove("struck"); }, STRUCK_MS);
  }

  // Flash an enemy marker when the player lands a melee hit on it.
  function strikeEnemy(enemyId) {
    const el = enemyMarkers.get(enemyId)?.el;
    if (!el) return;
    el.classList.remove("struck");
    void el.offsetWidth;
    el.classList.add("struck");
    setTimeout(() => { if (!destroyed) el.classList.remove("struck"); }, STRUCK_MS);
  }

  // Play the player's melee hit: flash each enemy struck this step, and fade out
  // any the unit killed (dyingEnemyIds is read by the enemy-marker sync). Called
  // from executeHeadStep after the resolve, before the removing render.
  function playPlayerAttack(logSlice) {
    for (const effect of logSlice) {
      if (effect.type === "enemy-damaged") strikeEnemy(effect.enemyId);
      else if (effect.type === "enemy-killed" && effect.byPlayer) { strikeEnemy(effect.enemyId); dyingEnemyIds.add(effect.enemyId); }
    }
  }

  // Which survivors took a (non-fatal) melee hit from each enemy this phase, and
  // which enemies were cut down — derived from the turn's combat log entries so
  // the enemy-phase playback can shake the struck and flag the fallen.
  function combatVisualsFromLog(logSlice) {
    const damagedUnitIds = new Set(logSlice.filter((e) => e.type === "unit-damaged").map((e) => e.unitId));
    const struckByEnemy = {};
    for (const effect of logSlice) {
      if (effect.type === "contact" && effect.targetKind === "survivor" && damagedUnitIds.has(effect.targetId)) {
        (struckByEnemy[effect.enemyId] ??= new Set()).add(effect.targetId);
      }
    }
    const killedEnemyIds = new Set(logSlice.filter((e) => e.type === "enemy-killed").map((e) => e.enemyId));
    return { struckByEnemy, killedEnemyIds };
  }

  // While an enemy's "about to act" pulse plays, repaint every frame so its
  // vision cone's fill throbs in time with the marker ring (see the
  // fillOpacity sine in syncEnemiesAndCones). Returns a stop function.
  function startConePulseTicker(enemyId, displayEnemies) {
    actingEnemyId = enemyId;
    let raf = requestAnimationFrame(function tick() {
      if (destroyed) return;
      paintEnemyPhase(displayEnemies);
      raf = requestAnimationFrame(tick);
    });
    return () => { cancelAnimationFrame(raf); actingEnemyId = null; };
  }

  // A sleep used only during the enemy phase — it resolves early (so the phase
  // fast-forwards) when the player hits "Skip".
  function phaseSleep(ms) {
    if (enemyPhaseSkip) return Promise.resolve();
    return new Promise((resolve) => {
      const timer = setTimeout(() => { phaseSleepCancels = phaseSleepCancels.filter((c) => c !== cancel); resolve(); }, ms);
      const cancel = () => { clearTimeout(timer); resolve(); };
      phaseSleepCancels.push(cancel);
    });
  }

  // Fast-forward the enemy phase: resolve all pending waits and let the loops
  // snap enemies straight to their resolved poses.
  function skipEnemyAnimation() {
    if (!enemyPhaseRunning) return;
    enemyPhaseSkip = true;
    const cancels = phaseSleepCancels;
    phaseSleepCancels = [];
    for (const cancel of cancels) cancel();
  }

  // Slide + rotate one enemy from its old pose to its resolved one over a few
  // frames, repainting the cone each step so the turn/move reads clearly.
  async function tweenEnemy(enemyId, from, target, displayEnemies) {
    const start = [...from.position];
    const end = [...target.position];
    const startFacing = from.facingDegrees ?? 0;
    const endFacing = target.facingDegrees ?? startFacing;
    for (let frame = 1; frame <= ENEMY_MOVE_FRAMES; frame += 1) {
      if (destroyed || enemyPhaseSkip) break;
      const t = frame / ENEMY_MOVE_FRAMES;
      displayEnemies[enemyId] = { ...target, position: [lerp(start[0], end[0], t), lerp(start[1], end[1], t)], facingDegrees: lerpAngle(startFacing, endFacing, t) };
      paintEnemyPhase(displayEnemies);
      await phaseSleep(ENEMY_MOVE_MS / ENEMY_MOVE_FRAMES);
    }
    displayEnemies[enemyId] = { ...target, position: [...end], facingDegrees: endFacing };
    if (!destroyed) paintEnemyPhase(displayEnemies);
  }

  // Play out the enemy phase one enemy at a time: pulse → move/rotate → pause.
  // The map shows a per-enemy snapshot (displayEnemies) while the rest hold still.
  async function runEnemyPhase(before, next) {
    enemyPhaseRunning = true;
    enemyPhaseSkip = false;
    if (hooks.onPhaseChange) hooks.onPhaseChange("enemy"); // "ENEMY TURN" title card
    if (hooks.onChange) hooks.onChange(); // let the host swap End Turn → Skip
    const displayEnemies = {};
    for (const [id, enemy] of Object.entries(before.enemies)) displayEnemies[id] = { ...enemy, position: [...enemy.position] };
    const phaseLog = next.log.slice(before.log.length);
    const killsByEnemy = enemyKillsFromLog(phaseLog);
    const { struckByEnemy, killedEnemyIds } = combatVisualsFromLog(phaseLog);
    const reinforcementIds = Object.keys(next.enemies).filter((id) => !before.enemies[id]);

    paintEnemyPhase(displayEnemies);
    for (const enemyId of Object.keys(before.enemies)) {
      if (destroyed || enemyPhaseSkip) break;
      const target = next.enemies[enemyId];
      if (!target) {
        // Enemy cut down in melee this phase: give it a quick pulse and shake the
        // survivor who felled it before it drops off on the next paint.
        if (killedEnemyIds.has(enemyId)) {
          pulseEnemyMarker(enemyId, true);
          await phaseSleep(ENEMY_PULSE_STATIONARY_MS);
          pulseEnemyMarker(enemyId, false);
          for (const unitId of struckByEnemy[enemyId] ?? []) strikeUnit(unitId);
          await phaseSleep(ENEMY_SETTLE_STATIONARY_MS);
        }
        continue;
      }
      const from = displayEnemies[enemyId];
      const stationary = distanceInMeters(from.position, target.position) < STATIONARY_EPS_METERS
        && (from.facingDegrees ?? 0) === (target.facingDegrees ?? from.facingDegrees ?? 0);
      pulseEnemyMarker(enemyId, true);
      const stopConePulse = startConePulseTicker(enemyId, displayEnemies);
      await phaseSleep(stationary ? ENEMY_PULSE_STATIONARY_MS : ENEMY_PULSE_MS);
      stopConePulse();
      if (destroyed) break;
      if (stationary) {
        displayEnemies[enemyId] = { ...target, position: [...target.position] };
        if (!destroyed) paintEnemyPhase(displayEnemies);
      } else {
        await tweenEnemy(enemyId, from, target, displayEnemies);
      }
      pulseEnemyMarker(enemyId, false);
      for (const unitId of killsByEnemy[enemyId] ?? []) unitMarkers.get(unitId)?.el.classList.add("dying");
      for (const unitId of struckByEnemy[enemyId] ?? []) strikeUnit(unitId);
      await phaseSleep(stationary ? ENEMY_SETTLE_STATIONARY_MS : ENEMY_SETTLE_MS);
    }
    // Threat reinforcements arrive at the end (they pulse via the "spawned" class).
    for (const enemyId of reinforcementIds) {
      if (destroyed || enemyPhaseSkip) break;
      displayEnemies[enemyId] = { ...next.enemies[enemyId], position: [...next.enemies[enemyId].position] };
      paintEnemyPhase(displayEnemies);
      await phaseSleep(ENEMY_SETTLE_MS);
    }
    // Finalize: snap every enemy to its resolved pose and apply all kills — this
    // covers a normal finish and a mid-sequence Skip that broke out early.
    if (!destroyed) {
      for (const id of Object.keys(next.enemies)) displayEnemies[id] = { ...next.enemies[id], position: [...next.enemies[id].position] };
      for (const enemyId of Object.keys(before.enemies)) {
        pulseEnemyMarker(enemyId, false);
        for (const unitId of killsByEnemy[enemyId] ?? []) unitMarkers.get(unitId)?.el.classList.add("dying");
      }
      paintEnemyPhase(displayEnemies);
    }
    enemyPhaseRunning = false;
    enemyPhaseSkip = false;
  }

  function activeEngagementOrders(existingOrders) {
    const orders = {};
    const engagements = { ...(tacticalState.engagements || {}) };
    let changed = false;
    for (const [unitId, engagement] of Object.entries(engagements)) {
      const unit = tacticalState.units[unitId];
      const enemy = tacticalState.enemies[engagement.enemyId];
      if (!unit || !enemy || unit.status === "scavenging" || actedUnitIds.has(unitId) || existingOrders[unitId]) {
        if (!unit || !enemy || unit.status === "scavenging") {
          delete engagements[unitId];
          changed = true;
        }
        continue;
      }
      if (distanceInMeters(unit.position, enemy.position) > meleeRangeMeters) {
        delete engagements[unitId];
        changed = true;
        continue;
      }
      orders[unitId] = attackOrder(unit.position, enemy);
    }
    if (changed) tacticalState = { ...tacticalState, engagements };
    return orders;
  }

  async function advanceTurn() {
    if (enemyPhaseRunning || isTacticalSessionOver(tacticalState)) return;
    closeScavengeMenu();
    previewMode = false;
    previewOrder = null;
    hoveredBuildingKey = null;

    // 1. Player phase close-out: units act the moment they're ordered, so only
    //    the ones that DIDN'T act this turn still need a step here — their queued
    //    head order (or an in-progress scavenge) continues. Extracting the last
    //    survivor ends the supply run here.
    const playerOrders = {};
    for (const [unitId, queue] of Object.entries(orderQueues)) {
      if (!actedUnitIds.has(unitId) && queue.length) playerOrders[unitId] = queue[0];
    }
    for (const unit of Object.values(tacticalState.units)) {
      if (!actedUnitIds.has(unit.id) && !playerOrders[unit.id] && unit.status === "scavenging") playerOrders[unit.id] = unit.order ?? { type: "scavenge" };
    }
    Object.assign(playerOrders, activeEngagementOrders(playerOrders));
    const beforePlayer = tacticalState;
    tacticalState = resolveTacticalTurn(beforePlayer, playerOrders, { ...buildContext(), freezeEnemies: true });
    const playerLog = tacticalState.log.slice(beforePlayer.log.length);
    for (const effect of playerLog) {
      if (effect.type === "unit-extracted" && Array.isArray(effect.coordinate)) pendingExtractAnimations.set(effect.unitId, [...effect.coordinate]);
    }
    if (hooks.onLog) hooks.onLog(playerLog, tacticalState.turnNumber);
    advanceQueuesAfterTurn(playerLog, Object.keys(playerOrders));
    render();
    if (isTacticalSessionOver(tacticalState)) {
      if (hooks.onSessionOver) hooks.onSessionOver();
      return;
    }

    // 2. Enemy phase: resolve it, then reveal it one enemy at a time.
    spawnedThisTurnIds = new Set();
    const before = tacticalState;
    const next = resolveTacticalTurn(before, {}, buildContext());
    await runEnemyPhase(before, next);
    if (destroyed) return;

    // 3. Commit the resolved turn.
    const newLog = next.log.slice(before.log.length);
    tacticalState = next;
    cleanupQueuesForInactiveUnits();
    for (const effect of newLog) {
      if (effect.type === "enemy-spawned") spawnedThisTurnIds.add(effect.enemyId);
      if (effect.type === "unit-extracted" && Array.isArray(effect.coordinate)) pendingExtractAnimations.set(effect.unitId, [...effect.coordinate]);
    }
    if (hooks.onLog) hooks.onLog(newLog, tacticalState.turnNumber);
    skippedUnitIds.clear();
    actedUnitIds.clear(); // a new player turn — every unit can act again
    render();
    if (isTacticalSessionOver(tacticalState)) {
      if (hooks.onSessionOver) hooks.onSessionOver();
    } else if (hooks.onPhaseChange) {
      hooks.onPhaseChange("survivor"); // back to the player — "SURVIVOR TURN" card
    }
  }

  // --- Rendering -------------------------------------------------------------
  // Positions push straight to the map/markers on every state change; the
  // *visual* easing between turns comes from a CSS transition on the marker
  // elements' transform (see the tactical-unit-marker/tactical-enemy-marker
  // classes in styles.css), not a hand-rolled animation loop — the same
  // technique V3 used for its mission-car markers.

  // A short human label for a just-revealed building's status, tuned to match
  // how the building itself now renders (survivor / promising / stocked / empty).
  function buildingStatusLabel(feature, index, key) {
    const descriptor = tacticalState.survivorBuildings?.[String(key)];
    if (descriptor) {
      const profession = professionName(descriptor.backgroundId);
      return { text: profession ? `Survivor · ${profession}` : "Survivor", tone: "survivor" };
    }
    const pool = poolForFeature(feature, index);
    const count = pool?.contents.length ?? 0;
    if (count === 0) return { text: "Ransacked", tone: "empty" };
    if (pool.promising) return { text: `Promising · ${count} item${count === 1 ? "" : "s"}`, tone: "promising" };
    return { text: `${count} item${count === 1 ? "" : "s"}`, tone: "stocked" };
  }

  // A floating popup over a freshly revealed building showing its status (and a
  // real name, when the building actually has one — most don't, so the generic
  // "Building" line is dropped rather than stacked above every status). Pops in
  // (CSS) and fades after DISCOVERY_POPUP_MS. Anchored to the building centroid
  // via a marker so it tracks the (locked) supply-run camera.
  function spawnDiscoveryPopup(feature, index, key) {
    // Only real, named buildings (a shop/brand — explicitName) get a name line;
    // for the generic majority (name === the "Building" kind label) it's noise
    // stacked above the status, so show status alone.
    const name = feature.properties?.explicitName ? (feature.properties.name || "") : "";
    const status = buildingStatusLabel(feature, index, key);
    // Root is positioned by MapLibre (via transform) and only fades; the pop/
    // scale animation lives on an inner wrapper so it doesn't fight MapLibre's
    // positioning transform (same reasoning as the unit-marker CSS note).
    const el = document.createElement("div");
    el.className = `tactical-discovery-popup tone-${status.tone}`;
    const inner = document.createElement("div");
    inner.className = "inner";
    const ring = document.createElement("span");
    ring.className = "ring";
    if (name) {
      const nameEl = document.createElement("span");
      nameEl.className = "name";
      nameEl.textContent = name;
      inner.append(ring, nameEl);
    } else {
      inner.append(ring);
    }
    const statusEl = document.createElement("span");
    statusEl.className = "status";
    statusEl.textContent = status.text;
    inner.append(statusEl);
    el.append(inner);
    const marker = new maplibregl.Marker({ element: el, anchor: "bottom" }).setLngLat(featureCentroid(feature)).addTo(map);
    discoveryPopupMarkers.add(marker);
    setTimeout(() => {
      marker.remove();
      discoveryPopupMarkers.delete(marker);
    }, DISCOVERY_POPUP_MS);
  }

  // --- Potential Supplies tooltip ------------------------------------------

  // Find the (feature, index) for a building scavenge key by scanning the zone —
  // hover/pin only carry the key, but the tooltip needs the footprint to anchor.
  function featureForKey(key) {
    let index = -1;
    for (const feature of zone.buildings) {
      index += 1;
      if (String(buildingKey(feature, index)) === String(key)) return { feature, index };
    }
    return null;
  }

  // Build the tooltip DOM: an optional building name, then a fogged "Potential
  // Supplies" list — each slot is ??? unless a stable per-slot roll revealed its type
  // (category) or exact item (see items.mjs's itemPreviewReveal).
  function buildItemTooltipEl(feature, index, key) {
    const el = document.createElement("div");
    el.className = "tactical-item-tooltip";
    const card = document.createElement("div");
    card.className = "card";

    const name = feature.properties?.explicitName ? (feature.properties.name || "") : "";
    if (name) {
      const nameEl = document.createElement("div");
      nameEl.className = "b-name";
      nameEl.textContent = name;
      card.append(nameEl);
    }

    const ul = document.createElement("ul");
    const survivor = tacticalState.survivorBuildings?.[String(key)];
    if (survivor) {
      const head = document.createElement("div");
      head.className = "item-head";
      head.textContent = "Inside";
      const li = document.createElement("li");
      li.className = "lvl-exact";
      li.textContent = "Survivor";
      card.append(head);
      ul.append(li);
    } else {
      const head = document.createElement("div");
      head.className = "item-head";
      head.textContent = "Potential Supplies";
      card.append(head);
      const contents = poolForFeature(feature, index)?.contents ?? [];
      if (!contents.length) {
        const li = document.createElement("li");
        li.className = "empty";
        li.textContent = "picked over";
        ul.append(li);
      } else {
        for (const entry of itemPreviewReveal({ key: String(key), contents })) {
          const li = document.createElement("li");
          li.className = `lvl-${entry.level}`;
          li.textContent = entry.level === "exact"
            ? (getItem(entry.itemId)?.name ?? entry.itemId)
            : entry.level === "type" ? itemCategoryLabel(entry.category) : "???";
          ul.append(li);
        }
      }
    }
    card.append(ul);
    const tip = document.createElement("div");
    tip.className = "tip";
    card.append(tip);
    el.append(card);
    return el;
  }

  function removeItemTooltip() {
    if (itemTooltipMarker) { itemTooltipMarker.remove(); itemTooltipMarker = null; }
    itemTooltipKey = null;
  }

  function removeDangerTooltip() {
    if (dangerTooltipMarker) { dangerTooltipMarker.remove(); dangerTooltipMarker = null; }
  }

  // Show the "in danger" warning tooltip when the cursor is over a flagged unit
  // (in an enemy cone this turn, or predicted to enter one next). Re-checked each
  // render so it clears the instant the unit is no longer at risk.
  function syncDangerTooltip() {
    const unit = hoveredUnitId ? tacticalState?.units?.[hoveredUnitId] : null;
    // Match the red danger pulse exactly (see syncUnits): endangered + not
    // mid-scavenge, so the tooltip only ever explains a pulse that's visible.
    const show = unit && !enemyPhaseRunning && unit.status !== "scavenging" && endangeredUnitIds.has(hoveredUnitId);
    if (!show) { removeDangerTooltip(); return; }
    removeDangerTooltip();
    const el = document.createElement("div");
    el.className = "tactical-item-tooltip tactical-danger-tooltip";
    const card = document.createElement("div");
    card.className = "card";
    card.innerHTML = `<div class="warn-title">⚠ In danger</div><div class="warn-body">May be spotted this turn or next.</div><div class="tip"></div>`;
    el.append(card);
    dangerTooltipMarker = new maplibregl.Marker({ element: el, anchor: "bottom" }).setLngLat(unit.position).addTo(map);
  }

  // Show the tooltip over the active building: the hovered one while planning a
  // move, else the pinned one (clicked with no unit selected). Only ever for
  // discovered buildings; hidden entirely during the enemy phase.
  function syncItemTooltip() {
    const activeKey = enemyPhaseRunning ? null : (previewMode ? hoveredBuildingKey : pinnedTooltipKey);
    if (activeKey == null) { removeItemTooltip(); return; }
    if (String(activeKey) === String(itemTooltipKey) && itemTooltipMarker) return; // unchanged — leave it
    const found = featureForKey(activeKey);
    if (!found || !discoveredBuildingKeys.has(buildingKey(found.feature, found.index))) { removeItemTooltip(); return; }
    removeItemTooltip();
    const el = buildItemTooltipEl(found.feature, found.index, activeKey);
    itemTooltipMarker = new maplibregl.Marker({ element: el, anchor: "bottom" }).setLngLat(featureCentroid(found.feature)).addTo(map);
    itemTooltipKey = activeKey;
  }

  // Reveal every still-unknown building whose footprint a squad member's vision
  // circle now touches. Called each render; idempotent (a building reveals once).
  // A newly revealed building gets the celebratory popup + a brief bright outline
  // (recentlyDiscoveredKeys), which clears after DISCOVERY_PULSE_MS.
  function updateDiscovery() {
    const units = Object.values(tacticalState.units);
    if (!units.length) return;
    const celebrate = !silentReveal;
    silentReveal = false; // only the first pass (spawn reveal) is silent
    let index = -1;
    for (const feature of zone.buildings) {
      index += 1;
      const key = buildingKey(feature, index);
      if (discoveredBuildingKeys.has(key)) continue;
      if (!units.some((unit) => buildingWithinRadius(unit.position, feature, SCOUT_VISION_RADIUS_METERS))) continue;
      discoveredBuildingKeys.add(key);
      if (!celebrate) continue;
      recentlyDiscoveredKeys.add(key);
      spawnDiscoveryPopup(feature, index, key);
      setTimeout(() => {
        recentlyDiscoveredKeys.delete(key);
        if (!destroyed && tacticalState) syncBuildings();
      }, DISCOVERY_PULSE_MS);
    }
  }

  // The final destination of a unit's staged order (where it's headed this move),
  // or null if it has no pending movement — used to preview the reveal radius.
  function orderDestination(order) {
    if (!order) return null;
    if (Array.isArray(order.target)) return order.target;
    if (Array.isArray(order.path) && order.path.length) return order.path[order.path.length - 1];
    return null;
  }

  // The scout reveal radius, drawn as a ring around the destination the SELECTED
  // unit is currently AIMING toward (the live cursor preview only) — a planning
  // aid that follows the cursor. Deliberately NOT drawn for a committed order:
  // re-selecting a unit that already has orders would otherwise leave its stale
  // reveal circle sitting on the map until you committed a replacement.
  function syncVisionRanges() {
    const destinations = [];
    if (previewOrder) { const dest = orderDestination(previewOrder.order); if (dest) destinations.push(dest); }
    const features = destinations.map((destination) => ({
      type: "Feature",
      properties: {},
      geometry: { type: "Polygon", coordinates: [closedRing(geodesicCircle(destination, SCOUT_VISION_RADIUS_METERS, 48))] },
    }));
    map.getSource(SRC.visionRanges).setData(featureCollection(features));
  }

  // A dashed circle showing how far the selected unit can move THIS turn (its
  // move budget) — the "move circle" now that a move can't be planned past it.
  function syncMoveRange() {
    let features = [];
    const unit = !enemyPhaseRunning && previewMode && selectedUnitId ? tacticalState.units[selectedUnitId] : null;
    if (unit && unit.status !== "scavenging") {
      const radius = unit.moveMetersPerTurn ?? tunables.moveMetersPerTurn;
      if (radius > 0) features = [{
        type: "Feature", properties: {},
        geometry: { type: "Polygon", coordinates: [closedRing(geodesicCircle(unit.position, radius, 64))] },
      }];
    }
    map.getSource(SRC.moveRange).setData(featureCollection(features));
  }

  function syncBuildings() {
    let buildingIndex = -1;
    const scavengeTargetKeys = new Set();
    for (const [unitId, queue] of Object.entries(orderQueues)) {
      // While a unit is being re-planned (selected + aiming a new order), drop its
      // committed supply-run target so re-selecting it doesn't leave the old building lit
      // — the live preview below re-lights whatever you're now aiming at, matching
      // how the reveal ring follows the cursor.
      if (unitId === selectedUnitId && previewMode) continue;
      for (const order of queue) if (order?.type === "scavenge" && order.buildingId) scavengeTargetKeys.add(order.buildingId);
    }
    if (previewOrder?.order?.type === "scavenge" && previewOrder.order.buildingId) scavengeTargetKeys.add(previewOrder.order.buildingId);
    const revealKeys = previewRevealKeySet();
    const features = zone.buildings.map((feature) => {
      buildingIndex += 1;
      const key = buildingKey(feature, buildingIndex);
      const discovered = discoveredBuildingKeys.has(key);
      const pool = tacticalState.buildingContents?.[key] ?? null;
      const stocked = Boolean(pool && pool.contents.length > 0);
      const promising = stocked && pool.promising;
      const emptied = Boolean(pool && pool.contents.length === 0);
      const realState = promising ? "promising" : stocked ? "stocked" : emptied ? "emptied" : "unknown";
      const survivorBuilding = Boolean(tacticalState.survivorBuildings?.[String(key)]);
      // Until a scout's vision touches it, a building shows nothing of its real
      // contents — it renders as the uniform "unknown" slate and can't be
      // targeted. Once discovered, its true status is revealed. An undiscovered
      // building inside the previewed move's reveal radius is highlighted
      // (previewReveal) to show it *will* flip.
      return {
        ...feature,
        properties: {
          ...feature.properties,
          discovered,
          justDiscovered: recentlyDiscoveredKeys.has(key),
          previewReveal: !discovered && revealKeys.has(key),
          scavengeState: discovered ? realState : "unknown",
          stocked: discovered ? stocked : false,
          itemCount: discovered && stocked ? pool.contents.length : 0,
          sizeMeters: buildingSpanMeters(feature),
          survivorBuilding: discovered ? survivorBuilding : false,
          targeted: discovered && (scavengeTargetKeys.has(key) || hoveredBuildingKey === key),
        },
      };
    });
    map.getSource(SRC.buildings).setData(featureCollection(features));
  }

  // Undiscovered buildings that would flip to discovered if the selected unit
  // reached the destinations it currently has previewed/queued — the #6 "you'll
  // reveal these" highlight, extended to every step of a chain.
  function previewRevealKeySet() {
    const keys = new Set();
    if (!selectedUnitId) return keys;
    const destinations = [];
    for (const order of queueFor(selectedUnitId)) { const dest = orderDestination(order); if (dest) destinations.push(dest); }
    if (previewOrder && previewOrder.unitId === selectedUnitId) { const dest = orderDestination(previewOrder.order); if (dest) destinations.push(dest); }
    if (!destinations.length) return keys;
    let index = -1;
    for (const feature of zone.buildings) {
      index += 1;
      const key = buildingKey(feature, index);
      if (discoveredBuildingKeys.has(key)) continue;
      if (destinations.some((dest) => buildingWithinRadius(dest, feature, SCOUT_VISION_RADIUS_METERS))) keys.add(key);
    }
    return keys;
  }

  function syncItems() {
    const features = Object.values(tacticalState.scatteredItems).map((pickup) => {
      const item = getItem(pickup.itemId);
      return {
        type: "Feature",
        properties: { color: ITEM_CATEGORY_COLORS[item?.category] ?? DEFAULT_ITEM_COLOR, label: item?.name ?? pickup.itemId },
        geometry: { type: "Point", coordinates: pickup.position },
      };
    });
    map.getSource(SRC.items).setData(featureCollection(features));
  }

  // `enemiesOverride` lets the animated enemy phase paint a mid-move snapshot
  // (one enemy tweening at a time) instead of the committed tactical state.
  function syncEnemiesAndCones(previewState, enemiesOverride = null) {
    const coneFeatures = [];
    const ghostConeFeatures = [];
    const visionLineFeatures = [];
    const ghostLineFeatures = [];
    const ghostPointFeatures = [];
    const activeUnits = Object.values(tacticalState.units);
    const engagementEntries = Object.entries(tacticalState.engagements || {});
    const engagedEnemyIds = new Set(engagementEntries.map(([, engagement]) => engagement.enemyId));
    const seenEnemyIds = new Set();
    const occluders = visionOccluders(); // walls + intact doors in a defense; building footprints in a raid
    // Units caught in an enemy's cone this turn, or predicted to walk into one
    // next turn (from the preview state) — surfaced as a red danger pulse.
    const endangered = new Set();
    // Only the hovered/pinned enemy reveals its predicted next-move (ghost).
    const focusedEnemyId = hoveredEnemyId ?? pinnedEnemyId;

    for (const enemy of Object.values(enemiesOverride ?? tacticalState.enemies)) {
      seenEnemyIds.add(enemy.id);
      const color = stateColorFor(enemy.behavior);
      const facing = enemy.facingDegrees ?? 0;

      let entry = enemyMarkers.get(enemy.id);
      if (!entry) {
        const enemyId = enemy.id; // stable per marker slot — safe to close over
        const el = document.createElement("div");
        el.className = "tactical-enemy-marker";
        el.innerHTML = `<span class="hp-pips marker-pips enemy-pips"></span>`; // HP pips, shown once chipped
        // Guards read apart from patrollers purely by their wider scanning cone
        // now (they used to carry an "X" glyph); the marker itself is uniform.
        // Hover (or click to pin) an enemy to reveal its predicted next move.
        el.addEventListener("mouseenter", () => { hoveredEnemyId = enemyId; render(); });
        el.addEventListener("mouseleave", () => { if (hoveredEnemyId === enemyId) { hoveredEnemyId = null; render(); } });
        el.addEventListener("click", (event) => {
          event.stopPropagation();
          // With a unit armed, clicking an enemy attacks it; otherwise it pins
          // the enemy's predicted-move ghost.
          if (attackEnemyWithSelected(enemyId)) return;
          pinnedEnemyId = pinnedEnemyId === enemyId ? null : enemyId;
          render();
        });
        const marker = new maplibregl.Marker({ element: el, anchor: "center" }).setLngLat(enemy.position).addTo(map);
        entry = { marker, el };
        enemyMarkers.set(enemy.id, entry);
      }
      entry.el.style.background = color;
      // Combat HP pips, shown only once the enemy has been chipped (a full-HP
      // mook stays a clean dot); makes a 2-HP enemy visibly wear down.
      const eh = enemy.health;
      const chipped = Boolean(eh) && eh.current < eh.max;
      entry.el.classList.toggle("chipped", chipped);
      const enemyPips = entry.el.querySelector(".hp-pips");
      if (enemyPips) enemyPips.innerHTML = chipped ? healthPipsHtml(eh) : "";
      // Reinforcements that threat pulled in this turn pulse a red ring (CSS).
      entry.el.classList.toggle("spawned", spawnedThisTurnIds.has(enemy.id));
      entry.el.classList.toggle("pinned", enemy.id === pinnedEnemyId);
      entry.el.classList.toggle("engaged", engagedEnemyIds.has(enemy.id));
      entry.marker.setLngLat(enemy.position);

      // A stationary guard's cone is wider/longer than the default (enemyVisionFor).
      const vision = enemyVisionFor(enemy, tunables.coneDegrees, tunables.sightMeters);
      const coneRing = occludedConeRing(enemy.position, facing, { coneDegrees: vision.coneDegrees, rangeMeters: vision.sightMeters, occluders });
      // The enemy currently taking its turn gets its cone fill throbbed (via a
      // sine-driven opacity, repainted on a rAF ticker — see startConePulseTicker)
      // in addition to the marker's own CSS ring pulse, so the eye lands on it.
      const fillOpacity = enemy.id === actingEnemyId ? (0.55 + 0.35 * Math.sin(performance.now() / 90)) : 0.3;
      // A base defense hides the enemy cones entirely — raiders just charge the
      // base, so the fans are visual noise that buries the small footprint. The
      // danger pulse (a unit caught in a cone) below still fires from the same
      // isWithinVisionCone check, so the "you're spotted" cue is unaffected.
      if (!defense) coneFeatures.push({ type: "Feature", properties: { color, fillOpacity }, geometry: { type: "Polygon", coordinates: [closedRing(coneRing)] } });

      for (const unit of activeUnits) {
        const visible = isWithinVisionCone(enemy.position, unit.position, {
          facingDegrees: facing, coneDegrees: vision.coneDegrees, rangeMeters: vision.sightMeters, occluders,
        });
        if (visible) {
          visionLineFeatures.push({ type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: [enemy.position, unit.position] } });
          endangered.add(unit.id); // in a cone right now
        }
      }

      const previewEnemy = previewState?.enemies[enemy.id];
      // "Will move" now covers a predicted rotation too (e.g. a guard re-aiming
      // its cone), not just walking — so a rotator slow-pulses and reveals its
      // new facing on hover, same as a mover.
      const moved = Boolean(previewEnemy) && distanceInMeters(enemy.position, previewEnemy.position) > 0.5;
      const rotated = Boolean(previewEnemy) && facingDeltaDegrees(facing, previewEnemy.facingDegrees ?? facing) > 1;
      const willMove = moved || rotated;
      // An enemy that will move/rotate next turn slow-pulses so you know to inspect
      // it; its actual predicted move only draws when it's hovered/pinned (focused).
      entry.el.classList.toggle("will-move", willMove);
      if (previewEnemy) {
        const previewVision = enemyVisionFor(previewEnemy, tunables.coneDegrees, tunables.sightMeters);
        const previewFacing = previewEnemy.facingDegrees ?? 0;
        // Danger look-ahead: any survivor whose predicted position next turn lands
        // in this enemy's predicted cone lights up now, before they commit.
        for (const previewUnit of Object.values(previewState.units)) {
          if (isWithinVisionCone(previewEnemy.position, previewUnit.position, {
            facingDegrees: previewFacing, coneDegrees: previewVision.coneDegrees, rangeMeters: previewVision.sightMeters, occluders,
          })) endangered.add(previewUnit.id);
        }
        // The ghost draws only for the focused enemy. Always show the predicted
        // cone (rotated and/or moved); the move line + destination ring only when
        // it actually changes position (they'd be degenerate for a pure rotator).
        if (willMove && enemy.id === focusedEnemyId) {
          const ghostColor = stateColorFor(previewEnemy.behavior);
          if (moved) {
            ghostLineFeatures.push({ type: "Feature", properties: { color: ghostColor }, geometry: { type: "LineString", coordinates: [enemy.position, previewEnemy.position] } });
            ghostPointFeatures.push({ type: "Feature", properties: { color: ghostColor }, geometry: { type: "Point", coordinates: previewEnemy.position } });
          }
          const previewCone = occludedConeRing(previewEnemy.position, previewFacing, { coneDegrees: previewVision.coneDegrees, rangeMeters: previewVision.sightMeters, occluders });
          if (!defense) ghostConeFeatures.push({ type: "Feature", properties: { color: ghostColor }, geometry: { type: "Polygon", coordinates: [closedRing(previewCone)] } });
        }
      }
    }

    endangeredUnitIds = endangered;

    for (const [id, entry] of enemyMarkers) {
      if (!seenEnemyIds.has(id)) {
        enemyMarkers.delete(id);
        if (hoveredEnemyId === id) hoveredEnemyId = null;
        if (pinnedEnemyId === id) pinnedEnemyId = null;
        // An enemy the player just cut down fades out where it stood instead of
        // popping off; everything else (moved out of view etc.) removes at once.
        if (dyingEnemyIds.has(id)) {
          dyingEnemyIds.delete(id);
          entry.el.classList.add("dying");
          const marker = entry.marker;
          setTimeout(() => { if (!destroyed) marker.remove(); }, STRUCK_MS + 220);
        } else {
          entry.marker.remove();
        }
      }
    }

    map.getSource(SRC.cones).setData(featureCollection(coneFeatures));
    map.getSource(SRC.conesGhost).setData(featureCollection(ghostConeFeatures));
    map.getSource(SRC.visionLines).setData(featureCollection(visionLineFeatures));
    map.getSource(SRC.ghostLines).setData(featureCollection(ghostLineFeatures));
    map.getSource(SRC.ghostPoints).setData(featureCollection(ghostPointFeatures));
  }

  function syncUnits() {
    const orderPathFeatures = [];
    const waypointFeatures = [];
    const seenUnitIds = new Set();
    const activeUnits = Object.values(tacticalState.units);
    const engagedUnitIds = new Set(Object.keys(tacticalState.engagements || {}));

    for (const unit of activeUnits) {
      seenUnitIds.add(unit.id);
      // Draw the unit's whole order chain (committed queue, or the live preview
      // in its place / appended), with per-turn markers spanning the chain.
      if (unit.status !== "scavenging") {
        const orders = previewOrder && previewOrder.unitId === unit.id
          ? (previewOrder.chain ? [...queueFor(unit.id), previewOrder.order] : [previewOrder.order])
          : queueFor(unit.id);
        const segments = orderChainSegments(unit, orders);
        const combined = [];
        for (const { order, coords } of segments) {
          const color = ORDER_PATH_COLORS[order.type] ?? ORDER_PATH_COLORS.move;
          const drawn = order.type === "scavenge" ? [...coords, order.poiCoordinate ?? poiCoordinate] : coords;
          orderPathFeatures.push({ type: "Feature", properties: { color }, geometry: { type: "LineString", coordinates: drawn } });
          for (const point of coords) combined.push(point);
        }
        if (combined.length >= 2) {
          // A unit that already acted this turn continues its plan NEXT turn, so
          // its markers start at 2, not 1.
          const turnOffset = actedUnitIds.has(unit.id) ? 1 : 0;
          for (const waypoint of multiTurnWaypoints(unit, combined)) {
            waypointFeatures.push({ type: "Feature", properties: { turn: waypoint.turn + turnOffset, color: ORDER_PATH_COLORS.move }, geometry: { type: "Point", coordinates: waypoint.position } });
          }
        }
      }

      let entry = unitMarkers.get(unit.id);
      if (!entry) {
        const unitId = unit.id; // stable per marker slot — safe to close over
        const el = document.createElement("div");
        el.className = "tactical-unit-marker";
        el.innerHTML = `<span class="hp-pips marker-pips"></span><span class="dot"></span><span class="label"></span>`;
        el.addEventListener("mousedown", (event) => onUnitMouseDown(unitId, event));
        // Hovering an in-danger unit explains the red pulse (see syncDangerTooltip).
        el.addEventListener("mouseenter", () => { hoveredUnitId = unitId; syncDangerTooltip(); });
        el.addEventListener("mouseleave", () => { if (hoveredUnitId === unitId) { hoveredUnitId = null; syncDangerTooltip(); } });
        const marker = new maplibregl.Marker({ element: el, anchor: "center" }).setLngLat(unit.position).addTo(map);
        entry = { marker, el };
        unitMarkers.set(unit.id, entry);
      }
      entry.marker.setLngLat(unit.position);
      const scavenging = unit.status === "scavenging";
      const selected = unit.id === selectedUnitId;
      // A unit with future orders queued gets a subtle "ready" ring; a unit that
      // has already acted this turn dims until its next turn.
      entry.el.classList.toggle("ordered", hasOrders(unit.id) && !scavenging);
      entry.el.classList.toggle("acted", actedUnitIds.has(unit.id) && !scavenging);
      entry.el.classList.toggle("engaged", engagedUnitIds.has(unit.id) && !scavenging);
      entry.el.classList.toggle("scavenging", scavenging);
      entry.el.classList.toggle("selected", selected && !scavenging);
      // In (or walking into) an enemy cone: a slow red halo pulse until the
      // threat clears. A scavenging unit is tucked inside a building, so skip it.
      entry.el.classList.toggle("danger", endangeredUnitIds.has(unit.id) && !scavenging);
      entry.el.querySelector(".label").textContent = scavenging ? `${unit.label ?? unit.id} · scavenging` : (unit.label ?? unit.id);
      // Combat HP as pips above the marker (same style as the roster cards), plus
      // a red-dot "hurt" cue below full.
      const hp = unit.health;
      const wounded = Boolean(hp) && hp.current < hp.max;
      entry.el.classList.toggle("hurt", wounded);
      const pipsEl = entry.el.querySelector(".hp-pips");
      if (pipsEl) pipsEl.innerHTML = healthPipsHtml(hp);
    }

    for (const [id, entry] of unitMarkers) {
      if (seenUnitIds.has(id)) continue;
      unitMarkers.delete(id);
      const extractCoord = pendingExtractAnimations.get(id);
      if (extractCoord) {
        // Extracted: move to the exact extract point and fade immediately.
        pendingExtractAnimations.delete(id);
        extractingMarkers.add(entry.marker);
        entry.marker.setLngLat(extractCoord);
        setTimeout(() => entry.el.classList.add("extracting"), EXTRACT_SLIDE_MS);
        setTimeout(() => { entry.marker.remove(); extractingMarkers.delete(entry.marker); }, EXTRACT_SLIDE_MS + EXTRACT_FADE_MS);
      } else {
        entry.marker.remove();
      }
    }

    map.getSource(SRC.orderPaths).setData(featureCollection(orderPathFeatures));
    map.getSource(SRC.waypoints).setData(featureCollection(waypointFeatures));
  }

  // Recruited survivors trailing the squad. Rendered like enemies (own marker
  // map, add/update/remove-stale lifecycle) but deliberately WITHOUT a mousedown
  // listener — that omission is what keeps them non-selectable. Each carries a
  // visible name label.
  function syncCompanions() {
    const companions = tacticalState.companions ?? {};
    const seen = new Set();
    for (const companion of Object.values(companions)) {
      seen.add(companion.id);
      let entry = companionMarkers.get(companion.id);
      if (!entry) {
        const el = document.createElement("div");
        el.className = "tactical-companion-marker";
        el.innerHTML = `<span class="dot"></span><span class="label"></span>`;
        const marker = new maplibregl.Marker({ element: el, anchor: "center" }).setLngLat(companion.position).addTo(map);
        entry = { marker, el };
        companionMarkers.set(companion.id, entry);
      }
      entry.marker.setLngLat(companionRenderLngLat(companion));
      entry.el.querySelector(".label").textContent = companion.name ?? "";
    }
    for (const [id, entry] of companionMarkers) {
      if (!seen.has(id)) { entry.marker.remove(); companionMarkers.delete(id); }
    }
  }

  // Where a companion's marker actually draws: COMPANION_GAP_PX behind its leader
  // in screen space (so it never overlaps and trails along the same path), pinned
  // to the leader rather than the raw sim position. Once the recruiter has left
  // the zone (extracting out) there's no leader to trail, so it falls back to the
  // sim position, which tactical-turn is walking to the exit.
  function companionRenderLngLat(companion) {
    const leader = tacticalState.units?.[companion.followUnitId];
    if (!leader) return companion.position;
    const leaderPx = map.project(leader.position);
    const companionPx = map.project(companion.position);
    let dx = companionPx.x - leaderPx.x;
    let dy = companionPx.y - leaderPx.y;
    const distance = Math.hypot(dx, dy);
    if (distance < 0.5) { dx = 0; dy = 1; } // overlapping (just recruited): sit just below
    else { dx /= distance; dy /= distance; }
    return map.unproject([leaderPx.x + dx * COMPANION_GAP_PX, leaderPx.y + dy * COMPANION_GAP_PX]).toArray();
  }

  function render() {
    if (!tacticalState) return;
    updateDiscovery(); // reveal buildings the squad's vision now touches
    syncBuildings();
    syncVisionRanges();
    syncMoveRange();
    syncItems();
    const previewState = computePreviewState();
    syncEnemiesAndCones(previewState);
    syncUnits();
    syncCompanions();
    syncItemTooltip();
    syncDangerTooltip();
    if (hooks.onChange) hooks.onChange();
  }

  // --- Pointer input -----------------------------------------------------

  // Build the provisional order for the cursor (hover preview). `chain` (shift
  // held) starts it from the queue's tail so it appends; otherwise from the unit.
  // Returns null when there's nothing to preview. Also updates hoveredBuildingKey.
  function computePreviewOrder(coordinate, chain) {
    const unit = tacticalState.units[selectedUnitId];
    if (!unit || unit.status === "scavenging") return null;
    const from = chainOriginFor(selectedUnitId, chain);
    // Clicking on/near an enemy is an attack: close in and strike it. A supply run
    // uses a generous 18 m grab so you don't need pixel-perfect aim. A defense all
    // but removes the ground grab (1.5 m — basically "did you click the dot") so a
    // move-click never gets hijacked into an attack while repositioning; you attack
    // a raider by clicking its marker directly (the enemy dot's own click handler,
    // reliable at the defense zoom) or just let it auto-engage when it's in range.
    const enemy = enemyNearCoordinate(coordinate, defense ? 1.5 : ENEMY_TARGET_CLICK_METERS);
    if (enemy) { hoveredBuildingKey = null; return attackOrder(from, enemy); }
    const survivor = survivorBuildingAt(coordinate);
    if (survivor) { hoveredBuildingKey = survivor.key; return recruitOrder(from, survivor.feature, survivor.key); }
    const building = scavengeableBuildingAt(coordinate);
    if (building) { hoveredBuildingKey = building.key; return scavengeOrder(from, building.feature, building.key, "fill"); }
    hoveredBuildingKey = null;
    const pickup = pickupAtCoordinate(coordinate);
    if (pickup) return pickupOrder(from, pickup);
    const order = moveOrder(from, coordinate);
    if (coordinateInExtractionBand(order.target)) order.type = "extract";
    return order;
  }

  function throttledPreviewRender() {
    const now = performance.now();
    const elapsed = now - lastPreviewAt;
    if (elapsed >= PREVIEW_RENDER_MS) {
      lastPreviewAt = now;
      render();
    } else if (!previewRenderTimer) {
      previewRenderTimer = setTimeout(() => {
        previewRenderTimer = null;
        lastPreviewAt = performance.now();
        render();
      }, PREVIEW_RENDER_MS - elapsed);
    }
  }

  // Arm hover-to-preview for a unit: the cursor previews an order (its trail +
  // reveal ring draw live) and a map click schedules it. Used by the unit marker
  // (mousedown) and the host's unit list (selectUnit). A mid-scavenge unit can be
  // selected to inspect or interrupt, but not directly re-ordered while inside.
  function armPreview(unitId) {
    selectedUnitId = unitId;
    const unit = unitId ? tacticalState?.units?.[unitId] : null;
    previewMode = Boolean(unit && unit.status !== "scavenging");
    previewOrder = null;
    hoveredBuildingKey = null;
    pinnedTooltipKey = null; // entering move-planning: the hovered building drives the tooltip now
    render();
  }

  // A unit marker grabbed directly: select + arm preview. The mousedown bubbles
  // up to a map "click", so stamp a suppression window to ignore that one click
  // (it must not immediately schedule a no-op order onto the unit's own tile).
  function onUnitMouseDown(unitId, event) {
    event.preventDefault();
    if (enemyPhaseRunning || !tacticalState.units[unitId]) return;
    // The bubbled map click fires in the same tick; a short window swallows just
    // that one without eating a deliberate follow-up click.
    suppressMapClickUntil = performance.now() + 120;
    // A pending item transfer: clicking a survivor hands them the item (if close).
    if (transferSelection) { hooks.onTransfer?.(transferTo(unitId)); return; }
    armPreview(unitId);
  }

  // Live preview: while a unit is armed, the cursor's position (and whether shift
  // is held, for chaining) builds a provisional order and redraws its trail +
  // reveal highlight, leading-edge throttled so the pathfinding stays cheap.
  function onMapMouseMove(event) {
    if (enemyPhaseRunning) return;
    const coordinate = event.lngLat.toArray();
    // Hover affordance (works with or without a unit selected): a scouted building
    // with supply/occupant info gets a pointer cursor + the focus border. The border
    // is driven by hoveredBuildingKey via syncBuildings' `targeted`.
    const hovered = scavengeableBuildingAt(coordinate) || survivorBuildingAt(coordinate);
    const hoverKey = hovered ? hovered.key : null;
    const hoverChanged = String(hoverKey) !== String(hoveredBuildingKey);
    map.getCanvas().style.cursor = hoverKey ? "pointer" : "crosshair";
    hoveredBuildingKey = hoverKey;

    if (previewMode && selectedUnitId && tacticalState.units[selectedUnitId]?.status !== "scavenging") {
      const now = performance.now();
      if (now - lastHoverPathAt < PREVIEW_RENDER_MS) { if (hoverChanged) throttledPreviewRender(); return; }
      lastHoverPathAt = now;
      const chain = false; // plan-ahead (shift-click chaining) temporarily disabled
      const order = computePreviewOrder(coordinate, chain); // re-sets hoveredBuildingKey (same value)
      previewOrder = order ? { unitId: selectedUnitId, order, chain } : null;
      throttledPreviewRender();
      return;
    }
    if (hoverChanged) throttledPreviewRender();
  }

  // Leaving the map canvas clears the building hover (border + pointer cursor).
  function onMapMouseOut() {
    if (enemyPhaseRunning) return;
    map.getCanvas().style.cursor = "crosshair";
    if (hoveredBuildingKey !== null) { hoveredBuildingKey = null; render(); }
  }

  // Commit the previewed order on a map click. A plain click moves the unit its
  // first step immediately (then it dims); shift-click appends a future step to
  // the chain (which runs on a later turn). Cancel any time until it acts.
  function onMapClick(event) {
    if (enemyPhaseRunning) return;
    if (performance.now() < suppressMapClickUntil) return; // the click that selected the marker
    // No unit selected: a click just pins the Potential Supplies tooltip on a scouted
    // building under the cursor, or clears it when clicking bare ground.
    if (!previewMode || !selectedUnitId) {
      const target = event.lngLat.toArray();
      const building = scavengeableBuildingAt(target) || survivorBuildingAt(target);
      pinnedTooltipKey = building ? building.key : null;
      render();
      return;
    }
    const unit = tacticalState.units[selectedUnitId];
    if (!unit || unit.status === "scavenging") return;
    const chain = Boolean(event.originalEvent?.shiftKey);
    const coordinate = event.lngLat.toArray();
    const clientX = event.originalEvent?.clientX ?? 0;
    const clientY = event.originalEvent?.clientY ?? 0;

    // Survivor building: recruiting is housing-gated, so keep its confirm menu.
    const survivor = survivorBuildingAt(coordinate);
    if (survivor) { openRecruitMenu(clientX, clientY, unit, survivor, chain); previewOrder = null; previewMode = false; render(); return; }

    const order = computePreviewOrder(coordinate, chain);
    if (!order) { render(); return; }
    commitOrder(selectedUnitId, order, chain);
    previewOrder = null;
    if (chain) {
      // Shift-click keeps the unit armed so you can keep chaining steps.
      render();
      return;
    }
    // A plain click shows the arrival notice for a scavenge/extract, then
    // deselects — the cursor stops previewing and the reveal ring clears.
    if (order.type === "scavenge") showArrivalNotice(clientX, clientY, "Survivor will scavenge upon arrival");
    else if (order.type === "extract") showArrivalNotice(clientX, clientY, "Survivor will extract upon arrival");
    selectedUnitId = null;
    previewMode = false;
    render();
  }

  // --- Interaction (dis/en)abling ------------------------------------------
  // The map's own nav gestures (pan/zoom/rotate) would fight with click-to-
  // order, so they're off for the supply run's whole duration — the camera is
  // locked by the host before mount() anyway (see app.js's startSupplyRun).

  const NAV_HANDLERS = ["dragPan", "scrollZoom", "boxZoom", "doubleClickZoom", "touchZoomRotate", "keyboard"];
  function setNavHandlersEnabled(enabled) {
    for (const name of NAV_HANDLERS) {
      const handler = map[name];
      if (handler) enabled ? handler.enable() : handler.disable();
    }
  }

  // --- Public API (host chrome wires its buttons/sliders to these) ------

  function reset() {
    closeScavengeMenu();
    tacticalState = createSession();
    orderQueues = {};
    previewOrder = null;
    skippedUnitIds.clear();
    actedUnitIds.clear();
    selectedUnitId = null;
    previewMode = false;
    hoveredBuildingKey = null;
    pinnedTooltipKey = null;
    removeItemTooltip();
    hoveredEnemyId = null;
    pinnedEnemyId = null;
    hoveredUnitId = null;
    removeDangerTooltip();
    enemyPhaseRunning = false;
    enemyPhaseSkip = false;
    transferSelection = null;
    spawnedThisTurnIds = new Set();
    pendingExtractAnimations.clear();
    // A fresh supply run re-fogs every building; spawn-adjacent ones re-reveal on the
    // first render() below.
    discoveredBuildingKeys.clear();
    recentlyDiscoveredKeys.clear();
    silentReveal = true;
    for (const marker of discoveryPopupMarkers) marker.remove();
    discoveryPopupMarkers.clear();
    render();
    if (hooks.onPhaseChange) hooks.onPhaseChange("survivor"); // opening "SURVIVOR TURN" card
  }

  // Selecting a unit from the host chrome arms hover-preview too, so you can
  // pick a survivor in the list, sweep the cursor to aim, and click to commit.
  function selectUnit(id) {
    closeScavengeMenu();
    armPreview(id);
  }

  function stageDropForSelected(itemId) {
    const unit = selectedUnitId ? tacticalState.units[selectedUnitId] : null;
    if (enemyPhaseRunning || !unit || unit.status === "scavenging") return;
    commitOrder(unit.id, { type: "drop", itemId }, false);
    render();
  }

  function stageScavengeForSelected() {
    if (enemyPhaseRunning || !selectedUnitId || !poiBuilding) return;
    const unit = tacticalState.units[selectedUnitId];
    if (!unit || unit.status === "scavenging") return;
    const key = buildingKey(poiBuilding, zone.buildings.indexOf(poiBuilding));
    commitOrder(unit.id, scavengeOrder(chainOriginFor(unit.id, false), poiBuilding, key, "fill"), false);
    render();
  }

  // Push a boundary-crossing coordinate outward along the radial into the
  // extraction band, so an extract routes the unit onto the green exit lane
  // (where it auto-extracts) rather than stopping on the circle edge.
  function bandTargetForCrossing(coordinate) {
    const center = zone.centerCoordinate;
    const metersPerLon = Math.cos(coordinate[1] * Math.PI / 180) * 111320;
    const east = (coordinate[0] - center[0]) * metersPerLon;
    const north = (coordinate[1] - center[1]) * 111320;
    const length = Math.hypot(east, north) || 1;
    const push = EXTRACTION_BAND_METERS * 0.5;
    return [coordinate[0] + (east / length) * push / metersPerLon, coordinate[1] + (north / length) * push / 111320];
  }

  function stageExtractForSelected() {
    if (defense || enemyPhaseRunning || !selectedUnitId) return; // no extraction in a base defense
    const unit = tacticalState.units[selectedUnitId];
    if (!unit || unit.status === "scavenging") return;
    const from = chainOriginFor(unit.id, false);

    // Route onto the green extraction band (the exit lanes just outside the
    // circle) — pick the crossing whose band point we can actually reach,
    // scoring by distance + how close the terrain path actually lands.
    let best = null;
    for (const crossing of zone.boundaryCrossings) {
      const target = bandTargetForCrossing(crossing.crossingCoordinate);
      const routed = routePath(from, target);
      if (!routed.length) continue;
      const endGap = distanceInMeters(routed[routed.length - 1], target);
      const score = distanceInMeters(from, target) + endGap * 3;
      if (!best || score < best.score) best = { target, routed, endGap, score };
    }
    if (!best) return;
    const path = best.endGap > 0.5 ? [...best.routed, [...best.target]] : best.routed;
    commitOrder(unit.id, { type: "extract", path, target: [...best.target] }, false);
    render();
  }

  // Cancel a unit's whole scheduled order chain (the "Cancel previous orders"
  // action). A mid-scavenge unit is pulled back to the entry point so the player
  // can interrupt the planned/ongoing scavenge before ending the turn.
  function clearSelectedOrder() {
    if (!selectedUnitId) return;
    delete orderQueues[selectedUnitId];
    clearUnitEngagement(selectedUnitId, { notify: true });
    const unit = tacticalState.units[selectedUnitId];
    if (unit?.status === "scavenging") {
      const nextUnit = {
        ...unit,
        status: "active",
        position: Array.isArray(unit.entryCoordinate) ? [...unit.entryCoordinate] : [...unit.position],
        scavengeTurnsLeft: 0,
        entryCoordinate: null,
        scavengeBuildingId: null,
        scavengeMode: null,
        order: null,
      };
      tacticalState = { ...tacticalState, units: { ...tacticalState.units, [selectedUnitId]: nextUnit } };
    }
    previewOrder = null;
    render();
  }

  // Equip a bag item / unequip a loadout item for a unit — a free action (it
  // doesn't consume the unit's turn), scoped to that unit's own carried gear.
  function equipItem(unitId, itemId) {
    if (enemyPhaseRunning) return;
    const unit = tacticalState?.units?.[unitId];
    if (!unit) return;
    const next = equipCarriedItem(unit, itemId);
    if (next === unit) return;
    tacticalState = { ...tacticalState, units: { ...tacticalState.units, [unitId]: next } };
    render();
  }

  function unequipItem(unitId, itemId) {
    if (enemyPhaseRunning) return;
    const unit = tacticalState?.units?.[unitId];
    if (!unit) return;
    const next = unequipToBag(unit, itemId);
    if (next === unit) return;
    tacticalState = { ...tacticalState, units: { ...tacticalState.units, [unitId]: next } };
    render();
  }

  // --- Item transfer (hand a carried item to a nearby survivor) --------------

  // Pick up a bag item to give away; the next unit clicked receives it. Clicking
  // the same item again cancels. Transfers only succeed between units within the
  // scout radius of each other (they have to be physically close).
  function selectTransferItem(unitId, itemId) {
    if (enemyPhaseRunning) return;
    const unit = tacticalState?.units?.[unitId];
    if (!unit || !(unit.inventory ?? []).includes(itemId)) return;
    transferSelection = (transferSelection && transferSelection.fromUnitId === unitId && transferSelection.itemId === itemId)
      ? null
      : { fromUnitId: unitId, itemId };
    render();
  }

  function cancelTransfer() {
    if (!transferSelection) return;
    transferSelection = null;
    render();
  }

  // Hand the picked-up item to `targetUnitId`. Returns a result the host uses to
  // show feedback: { ok, reason: "too-far" | "no-room" | "same" | "none", itemId, targetName }.
  function transferTo(targetUnitId) {
    if (!transferSelection || enemyPhaseRunning) return { ok: false, reason: "none" };
    const { fromUnitId, itemId } = transferSelection;
    const source = tacticalState?.units?.[fromUnitId];
    const target = tacticalState?.units?.[targetUnitId];
    if (!source || !target) return { ok: false, reason: "none" };
    if (targetUnitId === fromUnitId) { transferSelection = null; render(); return { ok: false, reason: "same" }; }
    const targetName = target.label ?? targetUnitId;
    if (distanceInMeters(source.position, target.position) > SCOUT_VISION_RADIUS_METERS) {
      return { ok: false, reason: "too-far", itemId, targetName }; // keep the selection so they can retry a closer unit
    }
    if ((target.inventory ?? []).length >= (target.carryCapacity ?? 4)) {
      return { ok: false, reason: "no-room", itemId, targetName };
    }
    const sourceInventory = [...(source.inventory ?? [])];
    const index = sourceInventory.indexOf(itemId);
    if (index === -1) { transferSelection = null; render(); return { ok: false, reason: "none" }; }
    sourceInventory.splice(index, 1);
    const targetInventory = [...(target.inventory ?? []), itemId];
    tacticalState = {
      ...tacticalState,
      units: {
        ...tacticalState.units,
        [fromUnitId]: { ...source, inventory: sourceInventory },
        [targetUnitId]: { ...target, inventory: targetInventory },
      },
    };
    transferSelection = null;
    render();
    return { ok: true, itemId, targetName };
  }

  // Hold this unit for the turn: clear any queue and mark it acknowledged so the
  // End Turn idle check doesn't warn about it.
  function skipSelectedUnit() {
    if (!selectedUnitId) return;
    delete orderQueues[selectedUnitId];
    clearUnitEngagement(selectedUnitId, { notify: true });
    previewOrder = null;
    skippedUnitIds.add(selectedUnitId);
    render();
  }

  // Cycle selection to the next living, non-scavenging unit (a hotkey in the
  // host). Wraps around; arms preview on the newly selected unit.
  function selectNextUnit(direction = 1) {
    const ids = Object.values(tacticalState.units).filter((unit) => unit.status !== "scavenging").map((unit) => unit.id);
    if (!ids.length) return;
    const current = ids.indexOf(selectedUnitId);
    const next = ids[((current + direction) % ids.length + ids.length) % ids.length] ?? ids[0];
    closeScavengeMenu();
    armPreview(next);
  }

  function idleUnitIds() {
    return Object.values(tacticalState?.units ?? {})
      .filter((unit) => !actedUnitIds.has(unit.id) && !hasOrders(unit.id) && !tacticalState?.engagements?.[unit.id] && !skippedUnitIds.has(unit.id) && unit.status !== "scavenging")
      .map((unit) => unit.id);
  }

  function getAdvanceButtonState() {
    const idleIds = idleUnitIds();
    if (idleIds.length) return { mode: "needs-orders", label: "End Turn \u25b6", idleIds };
    const hasExecutableOrders = Object.values(tacticalState?.units ?? {}).some((unit) => {
      if (actedUnitIds.has(unit.id) || skippedUnitIds.has(unit.id)) return false;
      return hasOrders(unit.id) || Boolean(tacticalState?.engagements?.[unit.id]) || unit.status === "scavenging";
    });
    if (hasExecutableOrders) return { mode: "planned", label: "Complete Planned Orders \u25b6", idleIds };
    return { mode: "spent", label: "End Turn \u25b6", idleIds };
  }

  function applyPlaytestTurnStates() {
    if (!tacticalState || enemyPhaseRunning) return;
    const ids = Object.keys(tacticalState.units);
    const enemies = Object.values(tacticalState.enemies);
    if (ids[0]) {
      const unit = tacticalState.units[ids[0]];
      actedUnitIds.add(ids[0]);
      if (unit) {
        const target = [unit.position[0] + 0.00008, unit.position[1] + 0.00002];
        orderQueues[ids[0]] = [moveOrder(unit.position, target)];
      } else delete orderQueues[ids[0]];
      clearUnitEngagement(ids[0]);
    }
    if (ids[1]) {
      const unit = tacticalState.units[ids[1]];
      if (unit) {
        const target = [unit.position[0] + 0.00008, unit.position[1] + 0.00002];
        orderQueues[ids[1]] = [moveOrder(unit.position, target)];
        actedUnitIds.delete(ids[1]);
        clearUnitEngagement(ids[1]);
      }
    }
    if (ids[2] && enemies[0]) {
      const unit = tacticalState.units[ids[2]];
      const enemy = enemies[0];
      if (unit) {
        const engagedEnemy = { ...enemy, position: [unit.position[0] + 0.00001, unit.position[1]], facingDegrees: 180 };
        tacticalState = {
          ...tacticalState,
          enemies: { ...tacticalState.enemies, [enemy.id]: engagedEnemy },
          engagements: { ...(tacticalState.engagements || {}), [ids[2]]: { enemyId: enemy.id } },
        };
        actedUnitIds.delete(ids[2]);
        delete orderQueues[ids[2]];
        selectedUnitId = ids[2];
        previewMode = false;
        previewOrder = null;
      }
    }
    render();
  }

  function setTunable(key, value) {
    tunables[key] = value;
    if (key === "moveMetersPerTurn" && tacticalState) {
      for (const unit of Object.values(tacticalState.units)) unit.moveMetersPerTurn = value;
    }
    render();
  }

  function destroy() {
    destroyed = true;
    closeScavengeMenu();
    if (previewRenderTimer) { clearTimeout(previewRenderTimer); previewRenderTimer = null; }
    map.off("mousemove", onMapMouseMove);
    map.off("click", onMapClick);
    map.off("mouseout", onMapMouseOut);
    for (const entry of unitMarkers.values()) entry.marker.remove();
    for (const entry of enemyMarkers.values()) entry.marker.remove();
    for (const entry of companionMarkers.values()) entry.marker.remove();
    for (const marker of discoveryPopupMarkers) marker.remove(); // any live discovery popups
    discoveryPopupMarkers.clear();
    removeItemTooltip();
    removeDangerTooltip();
    for (const marker of extractingMarkers) marker.remove(); // any mid-fade extract markers
    unitMarkers.clear();
    enemyMarkers.clear();
    companionMarkers.clear();
    extractingMarkers.clear();
    pendingExtractAnimations.clear();
    unmountLayers();
    setNavHandlersEnabled(true);
    map.getCanvas().style.cursor = "";
  }

  mountLayers();
  setNavHandlersEnabled(false);
  // A base defense plays on a small footprint that's easy to lose on a phone, so
  // give the camera back its pan + zoom (tap-to-order still works — MapLibre tells
  // a tap from a drag). Left off: boxZoom (shift-drag would fight shift-chaining)
  // and keyboard. A supply run keeps the whole camera locked as before.
  if (defense) for (const name of ["dragPan", "scrollZoom", "doubleClickZoom", "touchZoomRotate"]) map[name]?.enable();
  map.getCanvas().style.cursor = "crosshair";
  map.on("mousemove", onMapMouseMove);
  map.on("click", onMapClick);
  map.on("mouseout", onMapMouseOut);
  reset();

  return {
    render,
    reset,
    advanceTurn,
    selectUnit,
    selectNextUnit,
    skipSelectedUnit,
    skipEnemyAnimation,
    equipItem,
    unequipItem,
    selectTransferItem,
    transferTo,
    cancelTransfer,
    getTransferSelection: () => (transferSelection ? { ...transferSelection } : null),
    stageScavengeForSelected,
    stageExtractForSelected,
    clearSelectedOrder,
    stageDropForSelected,
    setTunable,
    destroy,
    getTunables: () => ({ ...tunables }),
    getState: () => tacticalState,
    // The head order of each unit's queue (what resolves next turn) — the host
    // chrome labels unit cards from this.
    getStagedOrders: () => currentOrdersMap(),
    getEngagements: () => ({ ...(tacticalState?.engagements || {}) }),
    getSelectedUnitId: () => selectedUnitId,
    // How many orders the selected unit has queued (the host shows a Cancel
    // button when it's > 0).
    getSelectedQueueLength: () => (selectedUnitId ? queueFor(selectedUnitId).length : 0),
    getThreat: () => tacticalState?.threat ?? 0,
    // Buildings the squad actually scouted this supply run (stringified keys) — only
    // these should persist their status to the overworld map on run end.
    getDiscoveredBuildingKeys: () => new Set([...discoveredBuildingKeys].map(String)),
    // Whether the animated enemy phase is playing (the host disables End Turn).
    isEnemyPhaseRunning: () => enemyPhaseRunning,
    // Units that already took their action this turn (spent — host greys them).
    getActedUnitIds: () => new Set(actedUnitIds),
    getSkippedUnitIds: () => new Set(skippedUnitIds),
    getAdvanceButtonState,
    applyPlaytestTurnStates,
    // Active units that did nothing this turn: they didn't act, have nothing
    // queued, weren't skipped, and aren't mid-scavenge — warned about on End Turn.
    getIdleUnitIds: idleUnitIds,
    isOver: () => isTacticalSessionOver(tacticalState),
    // Push refreshed base-defense overlay GeoJSON (walls/doors/labels rebuilt by
    // the host with the live breach state) into the def sources this view owns.
    setDefenseOverlay,
  };

  // Re-feed the base-defense overlay sources. The host owns the base geometry
  // (so this generic view stays free of base-geometry imports) and rebuilds the
  // GeoJSON with current door HP/breach state; this just setData's it.
  function setDefenseOverlay(overlay) {
    if (!defense || destroyed || !overlay) return;
    if (overlay.rooms) map.getSource(SRC.defRooms)?.setData(overlay.rooms);
    if (overlay.walls) map.getSource(SRC.defWalls)?.setData(overlay.walls);
    if (overlay.doors) map.getSource(SRC.defDoors)?.setData(overlay.doors);
    if (overlay.labels) map.getSource(SRC.defLabels)?.setData(overlay.labels);
  }
}
